import type {
  ChartType,
  ContractType,
  DrawingToolType,
  State,
  Timeframe,
  TradeDirection,
  TradeStyleKey,
} from "./types";
import type { ChartDataPoint } from "./data";
import {
  applyThemeToInterface,
  getThemeByName,
  type ChartThemeName,
} from "./theme";
import { addContractMarker, addTradeMarker } from "./trades";
import { goToLatest, shouldShowLatestButton } from "./time";
import { INDICATOR_META, type IndicatorType } from "./indicators";
import { liveDataManager } from "./liveData";
import { DRAWING_TOOL_CHANGE_EVENT } from "./drawingTools";
import { markStaticDirty } from "./dirty";
import { syncOverlayIndicatorPriceRange } from "./draw";
import { scheduleChartPersistence } from "./persistence";
import type { MarketSelection } from "./types";

const MAX_ACTIVE_INDICATORS = 4;
const MAX_PANEL_INDICATORS = 2;

type WorkspaceMenu =
  | "market"
  | "indicators"
  | "drawings"
  | "chartType"
  | "timeframe"
  | "settings"
  | null;

type MarketSymbol = ReturnType<typeof liveDataManager.getSymbols>[number];
export type MarketFeed = {
  getSymbols(): MarketSymbol[];
  getSelectedSymbol(): string;
  selectSymbol(symbol: string): void;
  addMarketListener(callback: () => void): void;
  removeMarketListener(callback: () => void): void;
  addListener(callback: (data: ChartDataPoint[]) => void): void;
  removeListener(callback: (data: ChartDataPoint[]) => void): void;
  getLatestTick(): ChartDataPoint | null;
};

type ControlData = {
  state: State;
  redraw: () => void;
};

export type MarketControlsData = ControlData & {
  liveData?: MarketFeed;
  marketSelection?: MarketSelection;
  onSelectSymbol?: (symbol: string) => void;
};

export type TimeframeControlsData = ControlData & {
  setTimeframe: (tf: Timeframe) => void;
};

export type SettingsControlsData = ControlData & {
  setTheme?: (theme: ChartThemeName) => void;
};

export type ChartTypeControlsData = ControlData;
export type DrawingToolControlsData = ControlData;
export type IndicatorControlsData = ControlData;
export type TradeControlsData = ControlData;

export type MarketControlsOptions = { data: MarketControlsData };
export type TimeframeControlsOptions = { data: TimeframeControlsData };
export type SettingsControlsOptions = { data: SettingsControlsData };
export type ChartTypeControlsOptions = { data: ChartTypeControlsData };
export type DrawingToolControlsOptions = { data: DrawingToolControlsData };
export type IndicatorControlsOptions = { data: IndicatorControlsData };
export type TradeControlsOptions = { data: TradeControlsData };

type IconName =
  | "areaChart"
  | "candles"
  | "chart"
  | "chevronDown"
  | "cursor"
  | "draw"
  | "fib"
  | "horizontalLine"
  | "indicators"
  | "lineChart"
  | "market"
  | "ohlc"
  | "priceLabel"
  | "range"
  | "ray"
  | "settings"
  | "text"
  | "timeframe"
  | "trendline"
  | "verticalLine"
  | "hollowCandle";

const TIMEFRAMES: Timeframe[] = [
  "1t",
  "1m",
  "2m",
  "3m",
  "5m",
  "10m",
  "15m",
  "30m",
  "1h",
  "2h",
  "4h",
  "8h",
  "1D",
];

const CHART_TYPES: Array<{ id: ChartType; label: string; icon: IconName }> = [
  { id: "line", label: "Line", icon: "lineChart" },
  { id: "area", label: "Area", icon: "areaChart" },
  { id: "candle", label: "Candles", icon: "candles" },
  { id: "hollow", label: "Hollow", icon: "hollowCandle" },
  { id: "ohlc", label: "OHLC", icon: "ohlc" },
];

const CHART_TYPE_ICON_BY_ID: Record<ChartType, IconName> = {
  line: "lineChart",
  area: "areaChart",
  candle: "candles",
  hollow: "hollowCandle",
  ohlc: "ohlc",
};

const CANDLE_BASED_CHART_TYPES: ChartType[] = ["candle", "hollow", "ohlc"];

function isCandleBasedChartType(chartType: ChartType): boolean {
  return CANDLE_BASED_CHART_TYPES.includes(chartType);
}

function isTickTimeframe(timeframe: Timeframe): boolean {
  return timeframe === "1t";
}

function canUseChartTypeOnTimeframe(chartType: ChartType, timeframe: Timeframe): boolean {
  return !isTickTimeframe(timeframe) || !isCandleBasedChartType(chartType);
}

const CHART_THEMES: Array<{ id: ChartThemeName; label: string; description: string }> = [
  { id: "light", label: "Light Theme", description: "" },
  { id: "dark", label: "Dark Theme", description: "High-contrast trading view" },
];

const DRAWING_TOOLS: Array<{
  id: DrawingToolType | null;
  label: string;
  icon: IconName;
  title: string;
}> = [
  {
    id: null,
    label: "Cursor",
    icon: "cursor",
    title: "Select and move drawings",
  },
  {
    id: "horizontal",
    label: "Horizontal",
    icon: "horizontalLine",
    title: "Draw horizontal price line",
  },
  {
    id: "vertical",
    label: "Vertical",
    icon: "verticalLine",
    title: "Draw vertical time line",
  },
  {
    id: "trendline",
    label: "Trendline",
    icon: "trendline",
    title: "Draw two-point trendline",
  },
  {
    id: "ray",
    label: "Ray",
    icon: "ray",
    title: "Draw a line that extends from an anchor",
  },
  {
    id: "extendedRectangle",
    label: "Range",
    icon: "range",
    title: "Draw a rectangle that extends right",
  },
  {
    id: "fibRetrace",
    label: "Fib Retrace",
    icon: "fib",
    title: "Draw fibonacci retracement levels",
  },
  { id: "text", label: "Text", icon: "text", title: "Place a text label" },
  {
    id: "priceLabel",
    label: "Price Label",
    icon: "priceLabel",
    title: "Place a price level label",
  },
];

function button(
  className: string,
  label: string,
  title?: string,
): HTMLButtonElement {
  const el = document.createElement("button");
  el.type = "button";
  el.className = className;
  el.innerText = label;
  if (title) el.title = title;
  return el;
}

function makePanel(
  title: string,
  eyebrow?: string,
  onClose?: () => void,
): HTMLDivElement {
  const panel = document.createElement("div");
  panel.className = "chart-floating-panel chart-dropdown-panel";
  panel.classList.add("chart-card");
  const head = document.createElement("div");
  head.className = "chart-panel-head";
  if (eyebrow) {
    const small = document.createElement("div");
    small.className = "chart-panel-eyebrow";
    small.innerText = eyebrow;
    head.appendChild(small);
  }
  const titleRow = document.createElement("div");
  titleRow.className = "chart-panel-headbar";
  const heading = document.createElement("div");
  heading.className = "chart-panel-title";
  heading.innerText = title;
  titleRow.appendChild(heading);
  if (onClose) {
    const close = document.createElement("button");
    close.type = "button";
    close.className = "chart-panel-close";
    close.title = `Close ${title}`;
    close.innerText = "×";
    close.onclick = onClose;
    titleRow.appendChild(close);
  }
  head.appendChild(titleRow);
  panel.appendChild(head);
  return panel;
}

function getMarketLabel(symbol: MarketSymbol | undefined): string {
  return (
    symbol?.display_name ?? symbol?.name ?? symbol?.symbol ?? "Unknown market"
  );
}

function getMarketCode(symbol: MarketSymbol | undefined): string {
  return symbol?.symbol ?? symbol?.name ?? symbol?.display_name ?? "";
}

function svgElement<K extends keyof SVGElementTagNameMap>(
  tagName: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tagName);
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
  return el;
}

function createIcon(name: IconName, className = "chart-icon", size: number = 18): SVGSVGElement {
  const svg = svgElement("svg", {
    class: className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "1.8",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "aria-hidden": "true",
    width: String(size),
    height: String(size),
  });

  const path = (d: string) => svg.appendChild(svgElement("path", { d }));
  const line = (x1: number, y1: number, x2: number, y2: number) =>
    svg.appendChild(svgElement("line", { x1: String(x1), y1: String(y1), x2: String(x2), y2: String(y2) }));
  const rect = (x: number, y: number, width: number, height: number, rx = 1.5) =>
    svg.appendChild(svgElement("rect", { x: String(x), y: String(y), width: String(width), height: String(height), rx: String(rx) }));
  const circle = (cx: number, cy: number, r: number) =>
    svg.appendChild(svgElement("circle", { cx: String(cx), cy: String(cy), r: String(r) }));

  switch (name) {
    case "areaChart":
      path("M3 17l5-5 4 3 6-8 3 10");
      path("M3 21h18");
      break;
    case "candles":
  // Regular candles - one green (up), one red (down)
  // First candle: UP (green)
  line(7, 4, 7, 20); // Wick
  svg.appendChild(svgElement("rect", {
    x: "5",
    y: "8",
    width: "4",
    height: "7",
    rx: "0.5",
    fill: "#22c55e",  // Green
    stroke: "#22c55e",
    "stroke-width": "1.8"
  }));
  
  // Second candle: DOWN (red)
  line(17, 4, 17, 20); // Wick
  svg.appendChild(svgElement("rect", {
    x: "15",
    y: "6",
    width: "4",
    height: "10",
    rx: "0.5",
    fill: "#ef4444",  // Red
    stroke: "#ef4444",
    "stroke-width": "1.8"
  }));
  break;

case "hollowCandle":
  // Hollow candles - one filled (green), one hollow (red)
  // First candle: FILLED (green up)
  line(7, 4, 7, 20); // Wick
  svg.appendChild(svgElement("rect", {
    x: "5",
    y: "8",
    width: "4",
    height: "7",
    rx: "0.5",
    fill: "#22c55e",  // Green - filled
    stroke: "#22c55e",
    "stroke-width": "1.8"
  }));
  
  // Second candle: HOLLOW (red down - empty inside)
  line(17, 4, 17, 20); // Wick
  svg.appendChild(svgElement("rect", {
    x: "15",
    y: "6",
    width: "4",
    height: "10",
    rx: "0.5",
    fill: "none",  // Hollow - empty
    stroke: "#ef4444",  // Red outline
    "stroke-width": "1.8"
  }));
  break;
    case "chart":
      path("M4 19V5");
      path("M4 19h16");
      path("M8 16v-5");
      path("M12 16V8");
      path("M16 16v-7");
      break;
    case "chevronDown":
      path("M7 10l5 5 5-5");
      break;
    case "cursor":
      path("M5 4l10 15 2-7 5-2L5 4z");
      break;
    case "draw":
      path("M4 20l4.5-1 10-10a2.1 2.1 0 0 0-3-3l-10 10L4 20z");
      path("M13.5 6.5l3 3");
      break;
    case "fib":
      path("M4 7h16");
      path("M6 12h12");
      path("M8 17h8");
      path("M5 4v16");
      break;
    case "horizontalLine":
      line(4, 12, 20, 12);
      break;
    case "indicators":
      path("M4 16c2.5-8 5.5-8 8 0s5.5 8 8 0");
      path("M4 8c2 3 4 3 6 0s4-3 6 0 3 3 4 1");
      break;
    case "lineChart":
      path("M4 17l4-5 4 3 7-9");
      path("M4 21h16");
      break;
    case "market":
      // Up arrow (left column)
      path("M7 16V6");
      path("M4 9l3-3 3 3");
      // Down arrow (right column)
      path("M17 8v10");
      path("M14 15l3 3 3-3");
      break;
    case "ohlc":
      line(7, 5, 7, 19);
      line(4, 9, 7, 9);
      line(7, 15, 10, 15);
      line(17, 5, 17, 19);
      line(14, 8, 17, 8);
      line(17, 14, 20, 14);
      break;
    case "priceLabel":
      path("M4 7h10l6 5-6 5H4z");
      path("M10 9.5v5");
      path("M12.5 10.5A2.5 2.5 0 0 0 10 9.5c-1.2 0-2 .5-2 1.3 0 2.1 5 1 5 3.1 0 .8-.8 1.4-2 1.4-1.1 0-2-.4-2.7-1.1");
      break;
    case "range":
      rect(5, 7, 14, 10);
      line(5, 4, 5, 20);
      line(19, 4, 19, 20);
      break;
    case "ray":
      path("M5 19L19 5");
      path("M13 5h6v6");
      break;
    case "settings":
      path(
        "M19.14 12.94c.04-.31.06-.62.06-.94s-.02-.63-.06-.94l2.03-1.58a.6.6 0 0 0 .14-.77l-1.92-3.32a.6.6 0 0 0-.75-.26l-2.39.96a7.34 7.34 0 0 0-1.62-.94l-.36-2.54A.6.6 0 0 0 13.7 2h-3.4a.6.6 0 0 0-.59.49l-.36 2.54c-.58.24-1.12.55-1.62.94l-2.39-.96a.6.6 0 0 0-.75.26L2.67 8.59a.6.6 0 0 0 .14.77l2.03 1.58c-.04.31-.06.62-.06.94s.02.63.06.94L2.81 14.4a.6.6 0 0 0-.14.77l1.92 3.32a.6.6 0 0 0 .75.26l2.39-.96c.5.39 1.04.7 1.62.94l.36 2.54a.6.6 0 0 0 .59.49h3.4a.6.6 0 0 0 .59-.49l.36-2.54c.58-.24 1.12-.55 1.62-.94l2.39.96a.6.6 0 0 0 .75-.26l1.92-3.32a.6.6 0 0 0-.14-.77l-2.03-1.46ZM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5Z",
      );
      break;
    case "text":
      path("M5 6h14");
      path("M12 6v12");
      path("M9 18h6");
      break;
    case "timeframe":
      circle(12, 12, 8);
      path("M12 7v5l3 2");
      break;
    case "trendline":
      path("M5 18L19 6");
      circle(5, 18, 1.5);
      circle(19, 6, 1.5);
      break;
    case "verticalLine":
      line(12, 4, 12, 20);
      break;
  }

  return svg;
}

function setIconButton(
  buttonEl: HTMLButtonElement,
  iconName: IconName,
  label?: string,
  options?: { showLabel?: boolean; iconSize?: number }
): void {
  const size = options?.iconSize || 18;
  buttonEl.replaceChildren(createIcon(iconName, "chart-icon", size));
  if (!label) return;
  const text = document.createElement("span");
  text.className = "chart-button-label";
  if (options?.showLabel) text.classList.add("is-visible");
  text.innerText = label;
  buttonEl.appendChild(text);
}

function appendTextTag<K extends keyof HTMLElementTagNameMap>(
  parent: HTMLElement,
  tagName: K,
  text: string,
  className?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tagName);
  if (className) el.className = className;
  el.innerText = text;
  parent.appendChild(el);
  return el;
}

function normalizeControlData<T extends ControlData>(
  arg1: T | T["state"],
  arg2?: T["redraw"],
): T {
  if (typeof arg1 === "object" && arg1 !== null && "state" in arg1 && "redraw" in arg1) {
    return arg1 as T;
  }
  if (!arg2) throw new Error("Missing redraw callback for control builder");
  return { state: arg1 as T["state"], redraw: arg2 } as T;
}


export function createTradingWorkspaceControls(
  state: State,
  redraw: () => void,
  setTimeframe: (tf: Timeframe) => void,
  liveData: MarketFeed = liveDataManager,
  marketSelection?: MarketSelection,
  options?: {
    showMarketSelector?: boolean;
    showTimeframeSelector?: boolean;
    showIndicatorsButton?: boolean;
    showDrawingsButton?: boolean;
    showSettingsButton?: boolean;
    showChartTypeButton?: boolean;
    iconSize?: number;
  }
): HTMLDivElement {
  const root = document.createElement("div") as HTMLDivElement & { 
    refreshTimeframeButton?: (tf: Timeframe) => void;
    destroy?: () => void;
  };
  root.className = "chart-workspace";

  // Default to showing all if options not provided
  const showMarketSelector = options?.showMarketSelector ?? true;
  const showTimeframeSelector = options?.showTimeframeSelector ?? true;
  const showIndicatorsButton = options?.showIndicatorsButton ?? true;
  const showDrawingsButton = options?.showDrawingsButton ?? true;
  const showSettingsButton = options?.showSettingsButton ?? true;
  const showChartTypeButton = options?.showChartTypeButton ?? true;
  
  // Set icon size - default to 18px
  const iconSize = options?.iconSize || 18;

  // Set CSS variable for icon size on root
  root.style.setProperty('--chart-icon-size', `${iconSize}px`);

  let activeMenu: WorkspaceMenu = null;
  const persistSetup = () => scheduleChartPersistence(state);

  const headerRow = document.createElement("div");
  headerRow.className = "chart-workspace-header";

  const marketWindow = document.createElement("div");
  marketWindow.className = "chart-market-window";
  
  // Conditionally hide market selector
  if (!showMarketSelector) {
    marketWindow.style.display = "none";
  }

  const marketButton = button("chart-market-card", "", "Change market");

let lastQuote: number | null = null;
let lastLength: number = 0;
let pollInterval: number | null = null;
// previousQuote is the price from the tick BEFORE the current one.
// It lives here (not inside renderMarketButton) so replaceChildren() on
// every render doesn't discard it. It advances to currentQuote AFTER each
// render, making each new tick compare against the one that just preceded it.
// Reset to null on market switch so the first tick of a new market shows "flat".
let previousQuote: number | null = null;
let trackedSymbol: string | null = null;

const checkAndUpdateMarketButton = () => {
  if (!showMarketSelector) return;

  const chartData = state.chartData;
  const currentLength = chartData.length;
  const currentQuote = chartData[currentLength - 1]?.quote ?? null;
  const currentSymbol = marketSelection?.symbol ?? liveData.getSelectedSymbol();

  // Reset previous price whenever the market switches so the first tick of the
  // new market doesn't inherit a stale comparison from the old one.
  if (currentSymbol !== trackedSymbol) {
    trackedSymbol = currentSymbol;
    previousQuote = null;
  }

  if (currentLength !== lastLength || currentQuote !== lastQuote) {
    // Advance the previous-price cursor BEFORE updating lastQuote so that
    // renderMarketButton can read previousQuote vs currentQuote correctly.
    // Only advance when the price itself changed (ignore length-only changes
    // like history loading where the last price stays the same).
    if (currentQuote !== null && currentQuote !== lastQuote) {
      previousQuote = lastQuote; // lastQuote is still the OLD current at this point
    }
    lastLength = currentLength;
    lastQuote = currentQuote;
    renderMarketButton();
  }
};

const renderMarketButton = () => {
  if (!showMarketSelector) return;
  
  const selectedSymbol = marketSelection?.symbol ?? liveData.getSelectedSymbol();
  const selectedMarket = liveData
    .getSymbols()
    .find(
      (item) =>
        item.symbol === selectedSymbol || item.name === selectedSymbol,
    );
  const label = getMarketLabel(selectedMarket);
  
  // ===== READ LATEST DATA FROM chartData =====
  const chartData = state.chartData;
  const latestTick = chartData.length > 0 ? chartData[chartData.length - 1] : null;
  const currentQuote = latestTick?.quote ?? 0;
  
  // Diff current price against the previous tick's price.
  // previousQuote is maintained in the outer closure and advances after each
  // render, so it always holds the price from just before this tick arrived.
  // Falls back to currentQuote (flat / no arrow) on the very first tick.
  const baseQuote = previousQuote ?? currentQuote;
  const diff = currentQuote - baseQuote;
  const diffPct = baseQuote ? (diff / baseQuote) * 100 : 0;
  const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  const precision = Math.max(
    0,
    Math.min(8, Number.isFinite(Number(selectedMarket?.precision)) ? Number(selectedMarket?.precision) : 3),
  );
  
  // ===== USE STATE MARKET STATUS =====
  const statusIsLive = state.isMarketLive ?? false;
  
  marketButton.replaceChildren();
  
  // Blinking live dot
  const isMobile = window.innerWidth <= 760;
  const dotSize = isMobile ? 12 : 16;
  const liveDot = document.createElement("canvas");
  liveDot.width = dotSize;
  liveDot.height = dotSize;
  liveDot.style.width = `${dotSize}px`;
  liveDot.style.height = `${dotSize}px`;
  liveDot.style.marginRight = isMobile ? "2px" : "6px";
  liveDot.style.flexShrink = "0";
  
  // Store reference to dot for animation
  let dotAnimationId: number | null = null;
  
  const drawDot = () => {
    const dotCtx = liveDot.getContext("2d");
    if (!dotCtx) return;
    
    const t = performance.now() / 1000;
    const pulse = (Math.sin(t * Math.PI * 2) + 1) / 2;
    const center = dotSize / 2;
    const outerR = (isMobile ? 3 : 4) + pulse * (isMobile ? 2 : 3);
    const innerR = isMobile ? 2 : 3;
    // ===== USE COLOR BASED ON DIRECTION =====
    let dotColor;
    if (statusIsLive) {
      dotColor = diff > 0 ? "#16a34a" : diff < 0 ? "#dc2626" : "#64748b";
    } else {
      dotColor = "#64748b";
    }
    
    dotCtx.clearRect(0, 0, dotSize, dotSize);
    
    if (statusIsLive) {
      dotCtx.beginPath();
      dotCtx.arc(center, center, outerR, 0, Math.PI * 2);
      dotCtx.fillStyle = dotColor + Math.round(pulse * 0x99).toString(16).padStart(2, "0");
      dotCtx.fill();
    }
    
    dotCtx.beginPath();
    dotCtx.arc(center, center, innerR, 0, Math.PI * 2);
    dotCtx.fillStyle = dotColor;
    dotCtx.fill();
    
    dotCtx.beginPath();
    dotCtx.arc(center, center, 1, 0, Math.PI * 2);
    dotCtx.fillStyle = "#ffffff";
    dotCtx.fill();
    
    // Cancel previous animation and schedule next
    if (dotAnimationId !== null) {
      cancelAnimationFrame(dotAnimationId);
    }
    dotAnimationId = requestAnimationFrame(drawDot);
  };
  
  // Clean up animation when button is re-rendered
  const cleanupDot = () => {
    if (dotAnimationId !== null) {
      cancelAnimationFrame(dotAnimationId);
      dotAnimationId = null;
    }
  };
  
  // Store cleanup on the button for later
  (marketButton as any)._cleanupDot = cleanupDot;
  
  drawDot();
  marketButton.appendChild(liveDot);

  // Market info with live price
  const copy = document.createElement("span");
  copy.className = `chart-market-copy is-${direction}`;
  const titleRow = document.createElement("span");
  titleRow.className = "chart-market-title-row";
  appendTextTag(titleRow, "strong", label);
  copy.appendChild(titleRow);
  
  // Price row with change - colored based on direction
  const priceRow = document.createElement("span");
  priceRow.className = "chart-market-price-row";
  const price = document.createElement("strong");
  price.className = "chart-market-price";
  const formattedPrice = currentQuote.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
  price.innerText = formattedPrice;
  
  const delta = document.createElement("span");
  delta.className = "chart-market-delta";
  const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "•";
  const signedDiff = `${diff >= 0 ? "+" : ""}${Math.abs(diff).toFixed(precision)}`;
  
  // ===== COLOR THE DELTA BASED ON DIRECTION =====
  if (statusIsLive) {
    delta.style.color = diff > 0 ? "#16a34a" : diff < 0 ? "#dc2626" : "#64748b";
  } else {
    delta.style.color = "#64748b";
  }
  
  delta.innerText = `${arrow} ${signedDiff} (${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(2)}%)`;
  priceRow.append(price, delta);
  copy.appendChild(priceRow);
  marketButton.appendChild(copy);

  const chevron = document.createElement("span");
  chevron.className = "chart-chevron";
  chevron.appendChild(createIcon("chevronDown", "chart-icon", Math.max(10, iconSize - 4)));
  marketButton.appendChild(chevron);
};

// UPDATE: Clean up dot animation in destroy
(root as HTMLDivElement & { destroy?: () => void }).destroy = () => {
  window.removeEventListener(DRAWING_TOOL_CHANGE_EVENT, refresh);
  document.removeEventListener("pointerdown", closeMenuOnOutsidePress, true);
  document.removeEventListener("touchstart", closeMenuOnOutsidePress, true);
  window.removeEventListener("resize", repositionMenu);
  window.removeEventListener("orientationchange", repositionMenu);
  window.removeEventListener("resize", syncMarketLayout);
  window.removeEventListener("orientationchange", syncMarketLayout);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", repositionMenu);
    window.visualViewport.removeEventListener("scroll", repositionMenu);
  }
  if (showMarketSelector) {
    liveData.removeMarketListener(marketListener);
    liveData.removeListener(dataListener);
    // Clean up dot animation
    if (marketButton && (marketButton as any)._cleanupDot) {
      (marketButton as any)._cleanupDot();
    }
  }
  if (pollInterval !== null) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  if (menuHost.parentNode) {
    menuHost.parentNode.removeChild(menuHost);
  }
};

  // Start polling (every 100ms)
  pollInterval = window.setInterval(checkAndUpdateMarketButton, 100);
  
  // ===== DATA LISTENER FOR LIVE PRICE UPDATES =====
  const marketListener = () => {
    if (showMarketSelector) {
      renderMarketButton();
    }
  };
  
  const dataListener = () => {
  if (showMarketSelector) {
      checkAndUpdateMarketButton();
    }
  };

  if (showMarketSelector) {
    renderMarketButton();
    liveData.addMarketListener(marketListener);
    liveData.addListener(dataListener);
    marketWindow.appendChild(marketButton);

    const initialData = state.chartData;
    lastLength = initialData.length;
    lastQuote = initialData[initialData.length - 1]?.quote ?? null;
    // Seed trackedSymbol so the first real tick doesn't trigger a spurious reset.
    trackedSymbol = marketSelection?.symbol ?? liveData.getSelectedSymbol();
    // previousQuote stays null — the very first live tick will show flat (no arrow)
    // which is correct: there's nothing to compare against yet.
  }

  const controlsWindow = document.createElement("div");
  controlsWindow.className = "chart-controls-window";

  const nav = document.createElement("div");
  nav.className = "chart-toolbar";
  
  // Set CSS variable on nav for scoped styling
  nav.style.setProperty('--chart-icon-size', `${iconSize}px`);

  // Compact icon-only market button — kept in the DOM for backwards
  // compatibility but stays hidden now that the full market card (icon,
  // name, live price) is shown on every breakpoint. See syncMarketLayout.
  const marketToolBtn = showMarketSelector ? button("chart-tool-button chart-market-tool-btn", "", "Market") : null;
  if (marketToolBtn) {
    setIconButton(marketToolBtn, "market", undefined, { iconSize });
    nav.appendChild(marketToolBtn);
  }

  // Create buttons conditionally
  const chartTypeBtn = showChartTypeButton ? button("chart-tool-button", "", "Chart type") : null;
  const timeframeBtn = showTimeframeSelector ? button("chart-tool-button chart-timeframe-trigger", state.timeframe, "Timeframe") : null;
  const indicatorsBtn = showIndicatorsButton ? button("chart-tool-button", "", "Indicators") : null;
  const drawingsBtn = showDrawingsButton ? button("chart-tool-button", "", "Drawing tools") : null;
  const settingsBtn = showSettingsButton ? button("chart-tool-button chart-settings-trigger", "", "Settings") : null;

  if (chartTypeBtn) {
    setIconButton(chartTypeBtn, CHART_TYPE_ICON_BY_ID[state.chartType], state.chartType, { iconSize });
    nav.appendChild(chartTypeBtn);
  }
  if (timeframeBtn) {
    setIconButton(timeframeBtn, "timeframe", state.timeframe.toUpperCase(), { showLabel: true, iconSize });
    nav.appendChild(timeframeBtn);
  }
  if (indicatorsBtn) {
    setIconButton(indicatorsBtn, "indicators", "Indicators", { iconSize });
    nav.appendChild(indicatorsBtn);
  }
  if (drawingsBtn) {
    setIconButton(drawingsBtn, "draw", "Drawings", { iconSize });
    nav.appendChild(drawingsBtn);
  }
  if (settingsBtn) {
    setIconButton(settingsBtn, "settings", "Settings", { iconSize });
    nav.appendChild(settingsBtn);
  }

  // Market card is now shown in full (icon, name, live price) on every
  // breakpoint — including mobile — sitting side-by-side with the toolbar.
  // The compact icon-only toolbar button is kept around but stays hidden.
  const syncMarketLayout = () => {
    if (showMarketSelector) {
      marketWindow.style.display = "";
      if (marketToolBtn) marketToolBtn.style.display = "none";
    }
  };
  syncMarketLayout();
  window.addEventListener("resize", syncMarketLayout);
  window.addEventListener("orientationchange", syncMarketLayout);

  const timeframeButtons = new Map<Timeframe, HTMLButtonElement>();

  // Create menu panels only for enabled controls
  const menuPanels: Partial<Record<Exclude<WorkspaceMenu, null>, HTMLDivElement>> = {};
  if (showMarketSelector) menuPanels.market = document.createElement("div");
  if (showChartTypeButton) menuPanels.chartType = document.createElement("div");
  if (showTimeframeSelector) menuPanels.timeframe = document.createElement("div");
  if (showIndicatorsButton) menuPanels.indicators = document.createElement("div");
  if (showDrawingsButton) menuPanels.drawings = document.createElement("div");
  if (showSettingsButton) menuPanels.settings = document.createElement("div");
  
  for (const panel of Object.values(menuPanels)) {
    if (panel) {
      panel.className = "chart-menu-panel-host";
    }
  }

  const menuHost = document.createElement("div");
  menuHost.className = "chart-menu-host";
  menuHost.style.position = "fixed";
  menuHost.style.left = "0px";
  menuHost.style.top = "0px";
  menuHost.style.zIndex = "999999";
  
  // Only append panels that exist
  for (const panel of Object.values(menuPanels)) {
    if (panel) menuHost.appendChild(panel);
  }
  // documentElement escapes Firefox backdrop-filter stacking context issues.
  document.documentElement.appendChild(menuHost);

  function positionMenuHost(): void {
    if (!activeMenu) {
      menuHost.style.display = "none";
      menuHost.dataset.menu = "";
      return;
    }

    // isMobile must be declared before trigger selection
    const isMobile = window.innerWidth <= 760;
    const viewportMargin = isMobile ? 10 : 16;

    let trigger: HTMLButtonElement | null = null;
    if (activeMenu === "market" && showMarketSelector) {
      // The full market card is visible on every breakpoint now, so it can
      // always act as the anchor for the dropdown.
      trigger = marketButton;
    } else if (activeMenu === "chartType" && showChartTypeButton && chartTypeBtn) {
      trigger = chartTypeBtn;
    } else if (activeMenu === "timeframe" && showTimeframeSelector && timeframeBtn) {
      trigger = timeframeBtn;
    } else if (activeMenu === "indicators" && showIndicatorsButton && indicatorsBtn) {
      trigger = indicatorsBtn;
    } else if (activeMenu === "drawings" && showDrawingsButton && drawingsBtn) {
      trigger = drawingsBtn;
    } else if (activeMenu === "settings" && showSettingsButton && settingsBtn) {
      trigger = settingsBtn;
    }
    
    if (!trigger) {
      menuHost.style.display = "none";
      menuHost.dataset.menu = "";
      return;
    }

    menuHost.style.display = "block";
    menuHost.dataset.menu = activeMenu;
    
    const triggerRect = trigger.getBoundingClientRect();
    
    // Compact widths — smaller on mobile to leave more chart visible
    let desiredWidth = 200;
    if (activeMenu === "market") desiredWidth = isMobile ? 210 : 230;
    else if (activeMenu === "settings") desiredWidth = isMobile ? 160 : 180;
    else if (activeMenu === "timeframe") desiredWidth = isMobile ? 180 : 210;
    else if (activeMenu === "indicators" || activeMenu === "drawings") desiredWidth = isMobile ? 180 : 290;
    else if (activeMenu === "chartType") desiredWidth = isMobile ? 180 : 210;

    const maxWidth = Math.max(160, window.innerWidth - viewportMargin * 2);
    const hostWidth = Math.min(desiredWidth, maxWidth);

    menuHost.style.position = "fixed";
    menuHost.style.right = "auto";
    menuHost.style.bottom = "auto";
    menuHost.style.width = `${hostWidth}px`;
    menuHost.style.maxWidth = `${hostWidth}px`;
    menuHost.style.transform = "none";

    const extraTopOffset = 8;

    // Use visualViewport height when available — it reflects the actual visible
    // area inside iframes and embedded contexts more accurately than innerHeight.
    const viewportH = (window.visualViewport?.height ?? window.innerHeight);

    // Desired max-height for the panel: generous fraction of the visible viewport.
    // Do NOT cap by availableBelow — on embedded/integrated sites triggerRect.bottom
    // can be large relative to innerHeight, making availableBelow tiny and cutting
    // the panel off. Instead we set a fixed max-height and let the panel scroll;
    // if it would overflow at the bottom we flip it to anchor from the bottom edge.
    // Drawings panel gets a compact height on mobile — just enough to show
    // the tool list + active drawings without dominating the screen.
    const desiredMaxHeight = isMobile
      ? activeMenu === "drawings"
        ? Math.round(viewportH * 0.35)   // ~35% on mobile for drawings
        : Math.round(viewportH * 0.55)   // 55% for everything else
      : 300;

    menuHost.style.maxHeight = `${desiredMaxHeight}px`;
    menuHost.style.overflowY = "hidden"; // host clips; inner panel scrolls

    // Always apply scroll to drawings & indicators panels —
    // the panel is mounted on <html> so .chart-workspace scoping never applies.
    if (activeMenu === "drawings" || activeMenu === "indicators") {
      const floatingPanel = menuHost.querySelector<HTMLElement>(".chart-floating-panel");
      if (floatingPanel) {
        floatingPanel.style.maxHeight = `${desiredMaxHeight}px`;
        floatingPanel.style.overflowY = "auto";
        (floatingPanel.style as CSSStyleDeclaration & { webkitOverflowScrolling: string }).webkitOverflowScrolling = "touch";
        floatingPanel.style.overscrollBehavior = "contain";
      }
    }

    let left = triggerRect.left;
    if (activeMenu === "settings") {
      left = triggerRect.right - hostWidth;
    }
    left = Math.min(
      Math.max(left, viewportMargin),
      Math.max(viewportMargin, window.innerWidth - hostWidth - viewportMargin)
    );

    // Firefox: backdrop-filter/transform ancestors shift the fixed containing block.
    // Subtract visualViewport offsets so the menu lands in the right viewport position.
    const vvLeft = window.visualViewport ? window.visualViewport.offsetLeft : 0;
    const vvTop  = window.visualViewport ? window.visualViewport.offsetTop  : 0;

    // Preferred position: just below the trigger button.
    const preferredTop = triggerRect.bottom + extraTopOffset;
    // Would the panel overflow the bottom of the visible viewport?
    const wouldOverflow = (preferredTop - vvTop) + desiredMaxHeight > viewportH - viewportMargin;
    // If so, anchor to bottom of viewport instead of top of trigger.
    const finalTop = wouldOverflow
      ? Math.max(vvTop + viewportMargin, viewportH - desiredMaxHeight - viewportMargin - vvTop)
      : preferredTop - vvTop;
    const finalLeft = left - vvLeft;

    menuHost.style.left = `${finalLeft}px`;
    menuHost.style.top  = `${finalTop}px`;

    void menuHost.offsetHeight;

    const actualRect = menuHost.getBoundingClientRect();
    const driftX = actualRect.left - finalLeft;
    const driftY = actualRect.top  - finalTop;
    if (Math.abs(driftX) > 1) menuHost.style.left = `${finalLeft - driftX}px`;
    if (Math.abs(driftY) > 1) menuHost.style.top  = `${finalTop  - driftY}px`;
  }

  function renderMenu() {
    for (const panel of Object.values(menuPanels)) {
      if (panel) panel.replaceChildren();
    }
    if (!activeMenu) return;
    
    const activeHost = menuPanels[activeMenu as keyof typeof menuPanels];
    if (!activeHost) return;

    // Market Panel
    if (activeMenu === "market" && showMarketSelector) {
      const panel = makePanel("Markets", "Synthetic indices", closeActiveMenu);
      const search = document.createElement("input");
      search.className = "chart-search";
      search.placeholder = "Search market";
      panel.appendChild(search);

      const rowsHost = document.createElement("div");
      const renderMarkets = () => {
        rowsHost.replaceChildren();
        const query = search.value.trim().toLowerCase();
        const selectedSymbol = marketSelection?.symbol ?? liveData.getSelectedSymbol();
        const markets = liveData
          .getSymbols()
          .filter((item) => item.is_active !== false)
          .filter((item) => {
            if (!query) return true;
            const label = getMarketLabel(item).toLowerCase();
            const code = getMarketCode(item).toLowerCase();
            const market = String(item.market ?? "").toLowerCase();
            const subgroup = String(item.subgroup ?? "").toLowerCase();
            return (
              label.includes(query) ||
              code.includes(query) ||
              market.includes(query) ||
              subgroup.includes(query)
            );
          })
          .sort((a, b) => {
            const aSelected =
              a.symbol === selectedSymbol || a.name === selectedSymbol;
            const bSelected =
              b.symbol === selectedSymbol || b.name === selectedSymbol;
            if (aSelected !== bSelected) return aSelected ? -1 : 1;
            return getMarketLabel(a).localeCompare(getMarketLabel(b));
          });

        if (markets.length === 0) {
          const empty = document.createElement("div");
          empty.className = "chart-empty-state";
          empty.innerText = query
            ? "No markets match your search"
            : "Loading markets...";
          rowsHost.appendChild(empty);
          return;
        }

        for (const market of markets) {
          const code =
            getMarketCode(market) || market.symbol || market.name || "";
          const row = button("chart-menu-row", "");
          const rowIcon = document.createElement("span");
          rowIcon.appendChild(createIcon("chart"));
          row.appendChild(rowIcon);
          appendTextTag(row, "strong", getMarketLabel(market));
          appendTextTag(row, "em", code);
          row.classList.toggle(
            "is-active",
            market.symbol === selectedSymbol || market.name === selectedSymbol,
          );
          row.onclick = () => {
            if (code) {
              liveData.selectSymbol(code);
              if (marketSelection) marketSelection.symbol = code;
            }
            activeMenu = null;
            if (showMarketSelector) renderMarketButton();
            refresh();
            redraw();
          };
          rowsHost.appendChild(row);
        }
      };

      search.oninput = () => renderMarkets();
      rowsHost.className = "chart-market-list";
      panel.appendChild(rowsHost);
      renderMarkets();
      activeHost.appendChild(panel);
    }

    // Chart Type Panel
    if (activeMenu === "chartType" && showChartTypeButton) {
      const panel = makePanel("Chart Type", undefined, closeActiveMenu);
      for (const type of CHART_TYPES) {
        const row = button("chart-menu-row", "");
        const disabled = !canUseChartTypeOnTimeframe(type.id, state.timeframe);
        row.disabled = disabled;
        row.classList.toggle("is-disabled", disabled);
        row.title = disabled
          ? "Candle-based chart types are unavailable on 1 tick."
          : type.label;
        const rowIcon = document.createElement("span");
        rowIcon.appendChild(createIcon(type.icon));
        row.appendChild(rowIcon);
        appendTextTag(row, "strong", type.label);
        row.onclick = () => {
          if (row.disabled) return;
          state.chartType = type.id;
          activeMenu = null;
          refresh();
          redraw();
        };
        if (state.chartType === type.id) row.classList.add("is-active");
        panel.appendChild(row);
      }
      activeHost.appendChild(panel);
    }

    // Timeframe Panel
    if (activeMenu === "timeframe" && showTimeframeSelector) {
      const panel = makePanel("Timeframes", "Chart interval", closeActiveMenu);
      const grid = document.createElement("div");
      grid.className = "chart-timeframe-menu";
      timeframeButtons.clear();
      for (const tf of TIMEFRAMES) {
        const tfButton = button("chart-timeframe-option", tf, `${tf} timeframe`);
        const disabled = tf === "1t" && isCandleBasedChartType(state.chartType);
        tfButton.disabled = disabled;
        tfButton.classList.toggle("is-disabled", disabled);
        tfButton.title = disabled
          ? "1 tick is unavailable for candle-based chart types."
          : `${tf} timeframe`;
        tfButton.onclick = () => {
          if (tfButton.disabled) return;
          setTimeframe(tf);
          activeMenu = null;
          refresh();
        };
        if (state.timeframe === tf) tfButton.classList.add("is-active");
        timeframeButtons.set(tf, tfButton);
        grid.appendChild(tfButton);
      }
      panel.appendChild(grid);
      activeHost.appendChild(panel);
    }

    // Settings Panel
    if (activeMenu === "settings" && showSettingsButton) {
      const panel = makePanel("Settings", "Theme selection", closeActiveMenu);
      panel.classList.add("chart-settings-panel");

      const activateTheme = (theme: ChartThemeName) => {
        if (state.themeName === theme) return;
        state.themeName = theme;
        applyThemeToInterface(theme);
        markStaticDirty();
        refresh();
        redraw();
      };

      const themeGrid = document.createElement("div");
      themeGrid.className = "chart-theme-grid";
      for (const theme of CHART_THEMES) {
        const token = getThemeByName(theme.id);
        const tile = button("chart-theme-tile", "", theme.label);
        tile.classList.toggle("is-active", state.themeName === theme.id);

        const swatch = document.createElement("span");
        swatch.className = "chart-theme-swatch";
        swatch.style.background = token.background;
        const accentDot = document.createElement("span");
        accentDot.style.background = token.down;
        const successDot = document.createElement("span");
        successDot.style.background = token.up;
        swatch.append(accentDot, successDot);

        const textWrap = document.createElement("div");
        textWrap.className = "chart-theme-tile-copy";
        const title = document.createElement("strong");
        title.innerText = theme.label;
        const desc = document.createElement("span");
        if (theme.description) {
          desc.innerText = theme.description;
        } else {
          desc.hidden = true;
        }
        textWrap.append(title, desc);

        tile.append(swatch, textWrap);
        tile.onclick = () => activateTheme(theme.id);
        themeGrid.appendChild(tile);
      }
      panel.appendChild(themeGrid);
      activeHost.appendChild(panel);
    }

    // Indicators Panel
    if (activeMenu === "indicators" && showIndicatorsButton) {
      const panel = makePanel("Indicators", "Select to add", closeActiveMenu);
      const select = document.createElement("select");
      select.className = "chart-select";
      const grouped: Array<{ label: string; values: IndicatorType[] }> = [
        {
          label: "Overlay",
          values: [
            "none",
            "sma",
            "ema",
            "bollinger",
            "donchian",
            "ma_envelope",
            "vwap",
            "rainbow_ma",
            "ichimoku",
            "parabolic_sar",
            "alligator",
            "zigzag",
            "fractal_bands",
            "pivot_points",
          ],
        },
        {
          label: "Panel",
          values: [
            "rsi",
            "macd",
            "stochastic",
            "aroon",
            "smi",
            "williams_r",
            "cci",
            "roc",
            "atr",
            "adx",
            "obv",
            "awesome",
            "dpo",
            "volume",
          ],
        },
      ];
      for (const group of grouped) {
        const optgroup = document.createElement("optgroup");
        optgroup.label = group.label;
        for (const value of group.values) {
          const option = document.createElement("option");
          option.value = value;
          option.innerText = INDICATOR_META[value].label;
          option.selected = state.indicatorType === value;
          optgroup.appendChild(option);
        }
        select.appendChild(optgroup);
      }
      
      const controls = document.createElement("div");
      controls.className = "chart-inline-controls";
      const period = document.createElement("input");
      period.type = "number";
      period.min = "2";
      period.max = "500";
      period.value = String(state.indicatorPeriod);
      const indicatorColor = document.createElement("input");
      indicatorColor.type = "color";
      indicatorColor.value = state.indicatorColor;
      indicatorColor.title = "Indicator color";
      const indicatorWidth = document.createElement("input");
      indicatorWidth.type = "number";
      indicatorWidth.min = "1";
      indicatorWidth.max = "8";
      indicatorWidth.value = String(state.indicatorLineWidth);
      indicatorWidth.title = "Indicator line width";
      
      const addSelectedIndicator = () => {
        if (
          state.indicatorType === "none" ||
          state.indicators.length >= MAX_ACTIVE_INDICATORS
        )
          return;
        const panelCount = state.indicators.filter(
          (indicator) => INDICATOR_META[indicator.type].placement === "panel",
        ).length;
        if (
          INDICATOR_META[state.indicatorType].placement === "panel" &&
          panelCount >= MAX_PANEL_INDICATORS
        )
          return;
        state.indicators = [
          ...state.indicators,
          {
            id: `${state.indicatorType}-${Date.now()}`,
            type: state.indicatorType,
            period: state.indicatorPeriod,
            color: state.indicatorColor,
            lineWidth: state.indicatorLineWidth,
          },
        ];
        syncOverlayIndicatorPriceRange(state);
        persistSetup();
        refresh();
        redraw();
      };
      
      select.onchange = () => {
        state.indicatorType = select.value as IndicatorType;
        addSelectedIndicator();
      };
      period.onchange = () => {
        const next = Number.parseInt(period.value, 10);
        state.indicatorPeriod = Number.isFinite(next)
          ? Math.max(2, Math.min(500, next))
          : 20;
        period.value = String(state.indicatorPeriod);
      };
      indicatorColor.oninput = () => {
        state.indicatorColor = indicatorColor.value;
      };
      indicatorWidth.onchange = () => {
        const next = Number.parseInt(indicatorWidth.value, 10);
        state.indicatorLineWidth = Number.isFinite(next)
          ? Math.max(1, Math.min(8, next))
          : 2;
        indicatorWidth.value = String(state.indicatorLineWidth);
      };
      controls.append(period, indicatorColor, indicatorWidth);
      panel.append(select, controls);

      const activeSection = document.createElement("div");
      activeSection.className = "chart-active-section";
      activeSection.innerHTML = `<div class="chart-section-title">Active Indicators (${state.indicators.length})</div>`;
      if (state.indicators.length > 0) {
        const clearAll = button("chart-danger-row", "Clear all");
        clearAll.onclick = () => {
          state.indicators = [];
          syncOverlayIndicatorPriceRange(state);
          persistSetup();
          refresh();
          redraw();
        };
        activeSection.appendChild(clearAll);
      }

      if (state.indicators.length === 0) {
        const empty = document.createElement("div");
        empty.className = "chart-empty-state";
        empty.innerText = "No active indicators";
        activeSection.appendChild(empty);
      }

      for (const indicator of state.indicators) {
        const row = document.createElement("div");
        row.className = "chart-active-row";

        const label = document.createElement("div");
        label.className = "chart-active-row-label";
        const meta = document.createElement("div");
        meta.className = "chart-active-meta";
        meta.innerHTML = `<span style="background:${indicator.color}"></span><strong>${INDICATOR_META[indicator.type].label}</strong>`;
        label.appendChild(meta);

        const controls = document.createElement("div");
        controls.className = "chart-active-controls";

        const periodEdit = document.createElement("input");
        periodEdit.type = "number";
        periodEdit.min = "2";
        periodEdit.max = "500";
        periodEdit.value = String(indicator.period);
        periodEdit.title = "Period";
        periodEdit.onchange = () => {
          const next = Number.parseInt(periodEdit.value, 10);
          indicator.period = Number.isFinite(next)
            ? Math.max(2, Math.min(500, next))
            : indicator.period;
          periodEdit.value = String(indicator.period);
          syncOverlayIndicatorPriceRange(state);
          persistSetup();
          redraw();
        };

        const colorEdit = document.createElement("input");
        colorEdit.type = "color";
        colorEdit.value = indicator.color;
        colorEdit.title = "Color";
        colorEdit.oninput = () => {
          indicator.color = colorEdit.value;
          const swatch = meta.querySelector("span");
          if (swatch) swatch.style.background = indicator.color;
          persistSetup();
          redraw();
        };

        const widthEdit = document.createElement("input");
        widthEdit.type = "number";
        widthEdit.min = "1";
        widthEdit.max = "8";
        widthEdit.value = String(indicator.lineWidth);
        widthEdit.title = "Line width";
        widthEdit.onchange = () => {
          const next = Number.parseInt(widthEdit.value, 10);
          indicator.lineWidth = Number.isFinite(next)
            ? Math.max(1, Math.min(8, next))
            : indicator.lineWidth;
          widthEdit.value = String(indicator.lineWidth);
          syncOverlayIndicatorPriceRange(state);
          persistSetup();
          redraw();
        };

        const remove = button("chart-icon-action", "×", "Remove indicator");
        remove.onclick = () => {
          state.indicators = state.indicators.filter(
            (item) => item.id !== indicator.id,
          );
          syncOverlayIndicatorPriceRange(state);
          persistSetup();
          refresh();
          redraw();
        };

        controls.append(periodEdit, colorEdit, widthEdit, remove);
        row.append(label, controls);
        activeSection.appendChild(row);
      }

      panel.appendChild(activeSection);
      activeHost.appendChild(panel);
    }

    // Drawings Panel
    if (activeMenu === "drawings" && showDrawingsButton) {
      const panel = makePanel("Drawing Tools", undefined, closeActiveMenu);
      for (const tool of DRAWING_TOOLS) {
        const row = button("chart-menu-row", "", tool.title);
        const rowIcon = document.createElement("span");
        rowIcon.appendChild(createIcon(tool.icon));
        row.appendChild(rowIcon);
        appendTextTag(row, "strong", tool.label);
        row.onclick = () => {
          state.activeDrawingTool = tool.id;
          state.currentDrawing = null;
          state.draggingDrawing = null;
          activeMenu = null;
          persistSetup();
          refresh();
          redraw();
        };
        const active =
          tool.id === null
            ? state.activeDrawingTool === null
            : state.activeDrawingTool === tool.id;
        if (active) row.classList.add("is-active");
        panel.appendChild(row);
      }
      
      const activeDrawings = document.createElement("div");
      activeDrawings.className = "chart-active-section";
      activeDrawings.innerHTML = `<div class="chart-section-title">Active Drawings (${state.drawings.length})</div>`;

      if (state.drawings.length > 0) {
        const clear = button("chart-danger-row", "Clear all");
        clear.onclick = () => {
          state.drawings = [];
          state.currentDrawing = null;
          state.selectedDrawingId = null;
          state.hoveredDrawingId = null;
          state.draggingDrawing = null;
          state.activeDrawingTool = null;
          activeMenu = null;
          persistSetup();
          refresh();
          redraw();
        };
        activeDrawings.appendChild(clear);
      }

      if (state.drawings.length === 0) {
        const empty = document.createElement("div");
        empty.className = "chart-empty-state";
        empty.innerText = "No active drawings";
        activeDrawings.appendChild(empty);
      }

      for (const drawing of state.drawings) {
        const row = document.createElement("div");
        row.className = "chart-active-row";
        if (state.selectedDrawingId === drawing.id)
          row.classList.add("is-selected");
        row.onclick = () => {
          state.selectedDrawingId = drawing.id;
          state.hoveredDrawingId = drawing.id;
          refresh();
          redraw();
        };

        const drawingLabel = DRAWING_TOOLS.find((t) => t.id === drawing.type)?.label ?? drawing.type;

        const label = document.createElement("div");
        label.className = "chart-active-row-label";
        const meta = document.createElement("div");
        meta.className = "chart-active-meta";
        meta.innerHTML = `<span style="background:${drawing.color}"></span><strong>${drawingLabel}</strong>`;
        label.appendChild(meta);

        const controls = document.createElement("div");
        controls.className = "chart-active-controls chart-active-controls-drawing";

        const colorEdit = document.createElement("input");
        colorEdit.type = "color";
        colorEdit.value = drawing.color;
        colorEdit.title = "Color";
        colorEdit.onclick = (event) => event.stopPropagation();
        colorEdit.oninput = () => {
          drawing.color = colorEdit.value;
          const swatch = meta.querySelector("span");
          if (swatch) swatch.style.background = drawing.color;
          persistSetup();
          redraw();
        };

        const widthEdit = document.createElement("input");
        widthEdit.type = "number";
        widthEdit.min = "1";
        widthEdit.max = "10";
        widthEdit.value = String(drawing.lineWidth);
        widthEdit.title = "Line width";
        widthEdit.onclick = (event) => event.stopPropagation();
        widthEdit.onchange = () => {
          const next = Number.parseInt(widthEdit.value, 10);
          drawing.lineWidth = Number.isFinite(next)
            ? Math.max(1, Math.min(10, next))
            : drawing.lineWidth;
          widthEdit.value = String(drawing.lineWidth);
          persistSetup();
          redraw();
        };

        const visibility = button(
          "chart-icon-action",
          drawing.visible ? "◉" : "○",
          drawing.visible ? "Hide drawing" : "Show drawing",
        );
        visibility.onclick = (event) => {
          event.stopPropagation();
          drawing.visible = !drawing.visible;
          persistSetup();
          refresh();
          redraw();
        };

        const remove = button("chart-icon-action", "×", "Remove drawing");
        remove.onclick = (event) => {
          event.stopPropagation();
          state.drawings = state.drawings.filter(
            (item) => item.id !== drawing.id,
          );
          if (state.selectedDrawingId === drawing.id)
            state.selectedDrawingId = null;
          persistSetup();
          refresh();
          redraw();
        };

        controls.append(colorEdit, widthEdit, visibility, remove);
        row.append(label, controls);
        activeDrawings.appendChild(row);
      }

      panel.appendChild(activeDrawings);
      activeHost.appendChild(panel);
    }
  }

  function refresh() {
    if (isTickTimeframe(state.timeframe) && isCandleBasedChartType(state.chartType)) {
      state.chartType = "line";
    }
    
    if (showMarketSelector) {
      marketButton.classList.toggle("is-active", activeMenu === "market");
      if (marketToolBtn) marketToolBtn.classList.toggle("is-active", activeMenu === "market");
    }
    if (showChartTypeButton && chartTypeBtn) {
      chartTypeBtn.classList.toggle("is-active", activeMenu === "chartType");
      setIconButton(
        chartTypeBtn,
        CHART_TYPE_ICON_BY_ID[state.chartType],
        state.chartType,
        { iconSize }
      );
      chartTypeBtn.title = `Chart type: ${
        CHART_TYPES.find((type) => type.id === state.chartType)?.label ??
        state.chartType
      }`;
    }
    if (showTimeframeSelector && timeframeBtn) {
      timeframeBtn.classList.toggle("is-active", activeMenu === "timeframe");
      setIconButton(timeframeBtn, "timeframe", state.timeframe.toUpperCase(), {
        showLabel: true,
        iconSize
      });
      timeframeBtn.title = `Timeframe: ${state.timeframe.toUpperCase()}`;
    }
    if (showIndicatorsButton && indicatorsBtn) {
      indicatorsBtn.classList.toggle("is-active", activeMenu === "indicators");
    }
    if (showDrawingsButton && drawingsBtn) {
      drawingsBtn.classList.toggle("is-active", activeMenu === "drawings");
    }
    if (showSettingsButton && settingsBtn) {
      settingsBtn.classList.toggle("is-active", activeMenu === "settings");
    }
    
    timeframeButtons.forEach((el, tf) =>
      el.classList.toggle("is-active", state.timeframe === tf),
    );
    renderMenu();
    window.requestAnimationFrame(positionMenuHost);
  }

  const closeActiveMenu = () => {
    if (!activeMenu) return;
    activeMenu = null;
    refresh();
  };

  const toggle = (menu: WorkspaceMenu) => {
    activeMenu = activeMenu === menu ? null : menu;
    refresh();
  };

  if (showMarketSelector) marketButton.onclick = () => toggle("market");
  if (showMarketSelector && marketToolBtn) marketToolBtn.onclick = () => toggle("market");
  if (showChartTypeButton && chartTypeBtn) chartTypeBtn.onclick = () => toggle("chartType");
  if (showTimeframeSelector && timeframeBtn) timeframeBtn.onclick = () => toggle("timeframe");
  if (showIndicatorsButton && indicatorsBtn) indicatorsBtn.onclick = () => toggle("indicators");
  if (showDrawingsButton && drawingsBtn) drawingsBtn.onclick = () => toggle("drawings");
  if (showSettingsButton && settingsBtn) settingsBtn.onclick = () => toggle("settings");
  
  window.addEventListener(DRAWING_TOOL_CHANGE_EVENT, refresh);
  
  const closeMenuOnOutsidePress = (event: Event) => {
    if (!activeMenu) return;
    const target = event.target as Node | null;
    if (!target) return;
    const insideMenu = menuHost.contains(target);
    const insideControls = controlsWindow.contains(target) || (showMarketSelector && marketWindow.contains(target));
    if (insideMenu || insideControls) return;
    closeActiveMenu();
  };
  
  const repositionMenu = () => {
    if (showMarketSelector) {
      renderMarketButton();
    }
    if (activeMenu) {
      window.requestAnimationFrame(positionMenuHost);
    }
  };

  document.addEventListener("pointerdown", closeMenuOnOutsidePress, true);
  document.addEventListener("touchstart", closeMenuOnOutsidePress, true);
  window.addEventListener("resize", repositionMenu);
  window.addEventListener("orientationchange", repositionMenu);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", repositionMenu);
    window.visualViewport.addEventListener("scroll", repositionMenu);
  }

  // ===== DESTROY FUNCTION WITH CLEANUP =====
  // ===== UPDATE DESTROY =====
  (root as HTMLDivElement & { destroy?: () => void }).destroy = () => {
    window.removeEventListener(DRAWING_TOOL_CHANGE_EVENT, refresh);
    document.removeEventListener("pointerdown", closeMenuOnOutsidePress, true);
    document.removeEventListener("touchstart", closeMenuOnOutsidePress, true);
    window.removeEventListener("resize", repositionMenu);
    window.removeEventListener("orientationchange", repositionMenu);
    window.removeEventListener("resize", syncMarketLayout);
    window.removeEventListener("orientationchange", syncMarketLayout);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", repositionMenu);
      window.visualViewport.removeEventListener("scroll", repositionMenu);
    }
    if (showMarketSelector) {
      liveData.removeMarketListener(marketListener);
      liveData.removeListener(dataListener);
    }
    // ===== ADD THIS: Clean up poll interval =====
    if (pollInterval !== null) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
    if (menuHost.parentNode) {
      menuHost.parentNode.removeChild(menuHost);
    }
  };

  // Only add controls if at least one is visible
  const hasAnyControls = showMarketSelector || showChartTypeButton || showTimeframeSelector || showIndicatorsButton || showDrawingsButton || showSettingsButton;
  
  if (hasAnyControls) {
    controlsWindow.append(nav);
    headerRow.append(marketWindow, controlsWindow);
  } else {
    headerRow.style.display = "none";
  }

  root.refreshTimeframeButton = (tf: Timeframe) => {
    if (showTimeframeSelector && timeframeBtn) {
      setIconButton(timeframeBtn, "timeframe", tf.toUpperCase(), { showLabel: true, iconSize });
      timeframeBtn.title = `Timeframe: ${tf.toUpperCase()}`;
      
      // Also update the button text to show the new timeframe
      const labelSpan = timeframeBtn.querySelector('.chart-button-label');
      if (labelSpan) {
        labelSpan.textContent = tf.toUpperCase();
      }
      
      // Update active state on timeframe options if menu is open
      if (activeMenu === "timeframe") {
        const activeHost = menuPanels.timeframe;
        if (activeHost) {
          const options = activeHost.querySelectorAll('.chart-timeframe-option');
          options.forEach(opt => {
            const optTf = opt.textContent;
            if (optTf === tf) {
              opt.classList.add('is-active');
            } else {
              opt.classList.remove('is-active');
            }
          });
        }
      }
    }
  };
  
  root.append(menuHost);
  root.append(headerRow);
  refresh();
  return root;
}

export function createFloatingLatestButton(
  state: State,
  redraw: () => void,
  paddingRatio: number = 0.3,
  onGoLatest?: () => void,
): HTMLButtonElement & { updateVisibility?: () => void } {
  const button = document.createElement("button") as HTMLButtonElement & {
    updateVisibility?: () => void;
  };
  button.innerText = "LATEST";
  button.title = "Go to latest data";

  // Style the button to appear in bottom right
  button.style.cssText = `
    position: absolute;
    bottom: 22px;
    right: 24px;
    padding: 8px 14px;
    background: var(--chart-control-elevated);
    color: var(--chart-text);
    border: 1px solid var(--chart-border);
    border-radius: 999px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    font-family: monospace;
    box-shadow: 0 14px 30px rgba(0,0,0,0.28);
    z-index: 1000;
    transition: all 0.2s ease;
    opacity: 0;
    transform: translateY(10px);
    pointer-events: none;
  `;

  // Add hover effect
  button.onmouseenter = () => {
    button.style.background = "var(--chart-control-hover)";
    button.style.transform = "scale(1.05)";
  };

  button.onmouseleave = () => {
    button.style.background = "var(--chart-control-elevated)";
    button.style.transform = "scale(1)";
  };

  // Function to update button visibility
  function updateVisibility() {
    const shouldShow = shouldShowLatestButton(state);

    if (shouldShow) {
      button.style.opacity = "1";
      button.style.transform = "translateY(0)";
      button.style.pointerEvents = "auto";
    } else {
      button.style.opacity = "0";
      button.style.transform = "translateY(10px)";
      button.style.pointerEvents = "none";
    }
  }

  // Click handler
  button.onclick = () => {
    onGoLatest?.();
    // Reset the suppress flag so auto-follow resumes
    state.suppressMarketFollow = false;
    state.marketHandoffFreezeUntil = 0;
    goToLatest(state, paddingRatio);
    updateVisibility();
    redraw();
  };

  // Store the update function on the button for external calls
  button.updateVisibility = updateVisibility;

  // Initial visibility check
  updateVisibility();

  return button;
}

// Update existing createChartTypeControls if needed
export function createChartTypeControls(options: ChartTypeControlsOptions): HTMLDivElement;
export function createChartTypeControls(
  state: State,
  redraw: () => void,
): HTMLDivElement;
export function createChartTypeControls(
  arg1: ChartTypeControlsOptions | State,
  arg2?: () => void,
): HTMLDivElement {
  const { state, redraw } = normalizeControlData<ChartTypeControlsData>(
    arg1 as unknown as ChartTypeControlsData,
    arg2,
  );
  const container = document.createElement("div");
  container.style.cssText =
    "display:flex;gap:8px;margin-top:8px;justify-content:center;";

  const types = [
    { id: "line", label: "Line Chart" },
    { id: "area", label: "Area Chart" },
    { id: "candle", label: "Candlestick" },
    { id: "hollow", label: "Hollow Candle" },
    { id: "ohlc", label: "OHLC" },
  ] as const;

  const buttons = new Map<(typeof types)[number]["id"], HTMLButtonElement>();

  function refresh() {
    buttons.forEach((btn, id) => {
      const disabled = !canUseChartTypeOnTimeframe(id, state.timeframe);
      btn.disabled = disabled;
      btn.style.opacity = disabled ? "0.45" : "1";
      btn.style.cursor = disabled ? "not-allowed" : "pointer";
      btn.style.background = state.chartType === id ? "#3b82f6" : "#e2e8f0";
      btn.style.color = state.chartType === id ? "white" : "#1e293b";
      btn.title = disabled
        ? "Candle-based chart types are unavailable on 1 tick."
        : btn.innerText;
    });
  }

  types.forEach(({ id, label }) => {
    const btn = document.createElement("button");
    btn.innerText = label;
    const disabled = !canUseChartTypeOnTimeframe(id, state.timeframe);
    btn.disabled = disabled;
    btn.title = disabled
      ? "Candle-based chart types are unavailable on 1 tick."
      : label;
    btn.style.cssText = `
      padding: 6px 16px;
      background: ${state.chartType === id ? "#3b82f6" : "#e2e8f0"};
      color: ${state.chartType === id ? "white" : "#1e293b"};
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s;
    `;
    btn.style.opacity = disabled ? "0.45" : "1";
    btn.style.cursor = disabled ? "not-allowed" : "pointer";

    btn.onmouseenter = () => {
      if (btn.disabled) return;
      if (state.chartType !== id) {
        btn.style.background = "#cbd5e1";
      }
    };

    btn.onmouseleave = () => {
      if (btn.disabled) return;
      if (state.chartType !== id) {
        btn.style.background = "#e2e8f0";
      }
    };

    btn.onclick = () => {
      if (btn.disabled) return;
      state.chartType = id;
      redraw();
      refresh();
    };

    container.appendChild(btn);
    buttons.set(id, btn);
  });

  refresh();
  return container;
}

export function createDrawingToolControls(options: DrawingToolControlsOptions): HTMLDivElement;
export function createDrawingToolControls(
  state: State,
  redraw: () => void,
): HTMLDivElement;
export function createDrawingToolControls(
  arg1: DrawingToolControlsOptions | State,
  arg2?: () => void,
): HTMLDivElement {
  const { state, redraw } = normalizeControlData<DrawingToolControlsData>(
    arg1 as unknown as DrawingToolControlsData,
    arg2,
  );
  const persistSetup = () => scheduleChartPersistence(state);
  const container = document.createElement("div");
  container.style.cssText =
    "display:flex;gap:8px;margin-top:8px;justify-content:center;align-items:center;flex-wrap:wrap;";

  const tools: Array<{
    id: DrawingToolType | null;
    label: string;
    title: string;
  }> = [
    { id: null, label: "Cursor", title: "Select and move drawings" },
    {
      id: "horizontal",
      label: "Horizontal",
      title: "Draw horizontal price line",
    },
    { id: "vertical", label: "Vertical", title: "Draw vertical time line" },
    { id: "trendline", label: "Trendline", title: "Draw two-point trendline" },
    {
      id: "ray",
      label: "Ray",
      title: "Draw a line that extends from anchor to one side",
    },
    {
      id: "extendedRectangle",
      label: "Ext Rectangle",
      title: "Draw a rectangle that extends to the right",
    },
    { id: "ellipse", label: "Ellipse", title: "Draw a two-point ellipse" },
    { id: "triangle", label: "Triangle", title: "Draw a three-point triangle" },
    {
      id: "channel",
      label: "Channel",
      title: "Draw a two-point price channel",
    },
    { id: "fibFan", label: "Fib Fan", title: "Draw fibonacci fan rays" },
    {
      id: "fibRetrace",
      label: "Fib Retrace",
      title: "Draw fibonacci retracement levels",
    },
    {
      id: "fibExtension",
      label: "Fib Ext",
      title: "Draw fibonacci extension levels",
    },
    {
      id: "polyline",
      label: "Polyline",
      title: "Draw multi-segment polyline (double-click to finish)",
    },
    { id: "text", label: "Text", title: "Place a text label" },
    {
      id: "priceLabel",
      label: "Price Label",
      title: "Place a price level label",
    },
  ];

  const buttons = new Map<DrawingToolType | "cursor", HTMLButtonElement>();

  const refresh = () => {
    for (const [id, button] of buttons) {
      const active =
        id === "cursor"
          ? state.activeDrawingTool === null
          : state.activeDrawingTool === id;
      button.style.background = active ? "#3b82f6" : "#e2e8f0";
      button.style.color = active ? "#ffffff" : "#1e293b";
    }
  };

  for (const tool of tools) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.innerText = tool.label;
    btn.title = tool.title;
    btn.style.cssText =
      "padding:6px 14px;background:#e2e8f0;color:#1e293b;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:500;transition:all 0.2s;font-family:monospace;";
    btn.onclick = () => {
      state.activeDrawingTool = tool.id;
      state.currentDrawing = null;
      state.draggingDrawing = null;
      persistSetup();
      refresh();
      redraw();
    };
    container.appendChild(btn);
    buttons.set(tool.id ?? "cursor", btn);
  }

  const clearButton = document.createElement("button");
  clearButton.type = "button";
  clearButton.innerText = "Clear Drawings";
  clearButton.title = "Remove all drawings";
  clearButton.style.cssText =
    "padding:6px 14px;background:#fee2e2;color:#991b1b;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:500;transition:all 0.2s;font-family:monospace;";
  clearButton.onclick = () => {
    state.drawings = [];
    state.currentDrawing = null;
    state.selectedDrawingId = null;
    state.hoveredDrawingId = null;
    state.draggingDrawing = null;
    state.activeDrawingTool = null;
    persistSetup();
    refresh();
    redraw();
  };
  container.appendChild(clearButton);

  window.addEventListener(DRAWING_TOOL_CHANGE_EVENT, refresh);
  refresh();
  return container;
}

export function createIndicatorControls(options: IndicatorControlsOptions): HTMLDivElement;
export function createIndicatorControls(
  state: State,
  redraw: () => void,
): HTMLDivElement;
export function createIndicatorControls(
  arg1: IndicatorControlsOptions | State,
  arg2?: () => void,
): HTMLDivElement {
  const { state, redraw } = normalizeControlData<IndicatorControlsData>(
    arg1 as unknown as IndicatorControlsData,
    arg2,
  );
  const persistSetup = () => scheduleChartPersistence(state);
  const container = document.createElement("div");
  container.className = "chart-config-stack";

  const editor = document.createElement("div");
  editor.className = "chart-config-editor";

  const label = document.createElement("span");
  label.innerText = "Indicator";
  label.className = "chart-control-label";
  editor.appendChild(label);

  const select = document.createElement("select");
  select.className = "chart-config-select";
  const overlayGroup = document.createElement("optgroup");
  overlayGroup.label = "Overlay";
  const panelGroup = document.createElement("optgroup");
  panelGroup.label = "Panel";
  const ordered: IndicatorType[] = [
    "none",
    "sma",
    "ema",
    "bollinger",
    "donchian",
    "ma_envelope",
    "vwap",
    "rainbow_ma",
    "ichimoku",
    "parabolic_sar",
    "alligator",
    "zigzag",
    "fractal_bands",
    "pivot_points",
    "rsi",
    "macd",
    "stochastic",
    "aroon",
    "smi",
    "williams_r",
    "cci",
    "roc",
    "atr",
    "adx",
    "obv",
    "awesome",
    "dpo",
    "volume",
  ];
  for (const value of ordered) {
    const el = document.createElement("option");
    el.value = value;
    el.innerText = INDICATOR_META[value].label;
    if (state.indicatorType === value) el.selected = true;
    if (INDICATOR_META[value].placement === "overlay" || value === "none")
      overlayGroup.appendChild(el);
    else panelGroup.appendChild(el);
  }
  select.appendChild(overlayGroup);
  select.appendChild(panelGroup);

  const periodInput = document.createElement("input");
  periodInput.type = "number";
  periodInput.min = "2";
  periodInput.max = "500";
  periodInput.step = "1";
  periodInput.value = String(state.indicatorPeriod);
  periodInput.className = "chart-config-input chart-config-number";
  periodInput.title = "Indicator period";

  const periodLabel = document.createElement("span");
  periodLabel.innerText = "Period";
  periodLabel.className = "chart-control-label";

  const colorLabel = document.createElement("span");
  colorLabel.innerText = "Color";
  colorLabel.className = "chart-control-label";

  const colorInput = document.createElement("input");
  colorInput.type = "color";
  colorInput.value = state.indicatorColor;
  colorInput.className = "chart-config-color";
  colorInput.title = "Indicator color";

  const widthLabel = document.createElement("span");
  widthLabel.innerText = "Width";
  widthLabel.className = "chart-control-label";

  const widthInput = document.createElement("input");
  widthInput.type = "number";
  widthInput.min = "1";
  widthInput.max = "8";
  widthInput.step = "1";
  widthInput.value = String(state.indicatorLineWidth);
  widthInput.className = "chart-config-input chart-config-number";
  widthInput.style.width = "64px";
  widthInput.title = "Indicator line width";

  const addButton = document.createElement("button");
  addButton.type = "button";
  addButton.innerText = "Add";
  addButton.className = "chart-config-button";

  const activeList = document.createElement("div");
  activeList.className = "chart-chip-list";

  const getPanelCount = () =>
    state.indicators.filter(
      (indicator) => INDICATOR_META[indicator.type].placement === "panel",
    ).length;

  const refreshActiveList = () => {
    activeList.replaceChildren();
    for (const indicator of state.indicators) {
      const chip = document.createElement("span");
      chip.className = "chart-chip";

      const swatch = document.createElement("span");
      swatch.className = "chart-chip-swatch";
      swatch.style.background = indicator.color;

      const text = document.createElement("span");
      text.innerText = `${INDICATOR_META[indicator.type].label} ${indicator.period}`;

      const remove = document.createElement("button");
      remove.type = "button";
      remove.innerText = "x";
      remove.title = `Remove ${INDICATOR_META[indicator.type].label}`;
      remove.className = "chart-chip-remove";
      remove.onclick = () => {
        state.indicators = state.indicators.filter(
          (item) => item.id !== indicator.id,
        );
        syncOverlayIndicatorPriceRange(state);
        persistSetup();
        refreshActiveList();
        refreshPeriodState();
        redraw();
      };

      chip.appendChild(swatch);
      chip.appendChild(text);
      chip.appendChild(remove);
      activeList.appendChild(chip);
    }

    if (state.indicators.length === 0) {
      const empty = document.createElement("span");
      empty.innerText = "No active indicators";
      empty.className = "chart-control-label";
      activeList.appendChild(empty);
    }
  };

  const refreshPeriodState = () => {
    const disabled = state.indicatorType === "none";
    const panelLimitReached =
      !disabled &&
      INDICATOR_META[state.indicatorType].placement === "panel" &&
      getPanelCount() >= MAX_PANEL_INDICATORS;
    const totalLimitReached =
      !disabled && state.indicators.length >= MAX_ACTIVE_INDICATORS;
    periodInput.disabled = disabled;
    colorInput.disabled = disabled;
    widthInput.disabled = disabled;
    addButton.disabled = disabled || panelLimitReached || totalLimitReached;
    addButton.innerText = totalLimitReached
      ? "Limit"
      : panelLimitReached
        ? "Panel Limit"
        : "Add";
    periodLabel.style.opacity = disabled ? "0.5" : "1";
    colorLabel.style.opacity = disabled ? "0.5" : "1";
    widthLabel.style.opacity = disabled ? "0.5" : "1";
    periodInput.style.opacity = disabled ? "0.6" : "1";
    colorInput.style.opacity = disabled ? "0.6" : "1";
    widthInput.style.opacity = disabled ? "0.6" : "1";
    addButton.style.opacity = addButton.disabled ? "0.55" : "1";
    addButton.style.cursor = addButton.disabled ? "not-allowed" : "pointer";
  };

      const addSelectedIndicator = () => {
        if (state.indicatorType === "none") return;
        if (state.indicators.length >= MAX_ACTIVE_INDICATORS) return;
        if (
          INDICATOR_META[state.indicatorType].placement === "panel" &&
      getPanelCount() >= MAX_PANEL_INDICATORS
    )
      return;

      state.indicators = [
        ...state.indicators,
        {
        id: `${state.indicatorType}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type: state.indicatorType,
        period: state.indicatorPeriod,
        color: state.indicatorColor,
        lineWidth: state.indicatorLineWidth,
        },
      ];
      syncOverlayIndicatorPriceRange(state);
      persistSetup();
      refreshActiveList();
      refreshPeriodState();
      redraw();
  };

  select.onchange = () => {
    const next = select.value;
    state.indicatorType = next as IndicatorType;
    refreshPeriodState();
    addSelectedIndicator();
  };

  const applyPeriod = () => {
    const parsed = Number.parseInt(periodInput.value, 10);
    const normalized = Number.isFinite(parsed)
      ? Math.max(2, Math.min(500, parsed))
      : 20;
    periodInput.value = String(normalized);
    if (state.indicatorPeriod !== normalized) {
      state.indicatorPeriod = normalized;
    }
    syncOverlayIndicatorPriceRange(state);
    persistSetup();
  };
  periodInput.oninput = applyPeriod;
  periodInput.onchange = applyPeriod;

  colorInput.oninput = () => {
    if (state.indicatorColor !== colorInput.value) {
      state.indicatorColor = colorInput.value;
    }
    persistSetup();
  };

  const applyWidth = () => {
    const parsed = Number.parseInt(widthInput.value, 10);
    const normalized = Number.isFinite(parsed)
      ? Math.max(1, Math.min(6, parsed))
      : 2;
    widthInput.value = String(normalized);
    if (state.indicatorLineWidth !== normalized) {
      state.indicatorLineWidth = normalized;
    }
    syncOverlayIndicatorPriceRange(state);
    persistSetup();
  };
  widthInput.oninput = applyWidth;
  widthInput.onchange = applyWidth;

  addButton.onclick = addSelectedIndicator;

  refreshPeriodState();
  refreshActiveList();
  editor.appendChild(select);
  editor.appendChild(periodLabel);
  editor.appendChild(periodInput);
  editor.appendChild(colorLabel);
  editor.appendChild(colorInput);
  editor.appendChild(widthLabel);
  editor.appendChild(widthInput);
  editor.appendChild(addButton);
  container.appendChild(editor);
  container.appendChild(activeList);

  return container;
}

export function createTradeControls(options: TradeControlsOptions): HTMLDivElement;
export function createTradeControls(
  state: State,
  redraw: () => void,
): HTMLDivElement;
export function createTradeControls(
  arg1: TradeControlsOptions | State,
  arg2?: () => void,
): HTMLDivElement {
  const { state, redraw } = normalizeControlData<TradeControlsData>(
    arg1 as unknown as TradeControlsData,
    arg2,
  );
  const container = document.createElement("div");
  container.className = "chart-control-row";

  const durationLabel = document.createElement("span");
  durationLabel.innerText = "Trade Duration";
  durationLabel.className = "chart-control-label";
  container.appendChild(durationLabel);

  const durationSelect = document.createElement("select");
  durationSelect.className = "chart-config-select";
  const durations = [
    { label: "5s", value: 5 },
    { label: "15s", value: 15 },
    { label: "30s", value: 30 },
    { label: "1m", value: 60 },
    { label: "2m", value: 120 },
  ];
  for (const d of durations) {
    const opt = document.createElement("option");
    opt.value = String(d.value);
    opt.innerText = d.label;
    if (d.value === 60) opt.selected = true;
    durationSelect.appendChild(opt);
  }
  container.appendChild(durationSelect);

  const place = (direction: TradeDirection) => {
    const ok = addTradeMarker(state, direction, Number(durationSelect.value));
    if (!ok) return;
    goToLatest(state, 0.3);
    redraw();
  };

  const riseBtn = document.createElement("button");
  riseBtn.type = "button";
  riseBtn.innerText = "Buy";
  riseBtn.className = "chart-config-button chart-config-button--buy";
  riseBtn.onclick = () => place("buy");

  const fallBtn = document.createElement("button");
  fallBtn.type = "button";
  fallBtn.innerText = "Sell";
  fallBtn.className = "chart-config-button chart-config-button--sell";
  fallBtn.onclick = () => place("sell");

  container.appendChild(riseBtn);
  container.appendChild(fallBtn);

  const styleWrap = document.createElement("div");
  styleWrap.className = "chart-control-group";

  const styleLabel = document.createElement("span");
  styleLabel.innerText = "Marker Style";
  styleLabel.className = "chart-control-label";
  styleWrap.appendChild(styleLabel);

  const keys: Array<{ key: TradeStyleKey; label: string }> = [
    { key: "pin", label: "Pin" },
    { key: "vertical", label: "Vertical" },
    { key: "zone", label: "Zone" },
    { key: "countdown", label: "Countdown" },
    { key: "animation", label: "Animation" },
  ];

  const keyChecks = new Map<TradeStyleKey, HTMLInputElement>();
  const allCheck = document.createElement("input");
  allCheck.type = "checkbox";
  allCheck.checked = keys.every(({ key }) => state.tradeStyles[key]);

  const allRow = document.createElement("label");
  allRow.className = "chart-control-row";
  const allText = document.createElement("span");
  allText.innerText = "All";
  allRow.appendChild(allCheck);
  allRow.appendChild(allText);
  styleWrap.appendChild(allRow);

  const refreshAll = () => {
    allCheck.checked = keys.every(({ key }) => state.tradeStyles[key]);
  };

  allCheck.onchange = () => {
    const next = allCheck.checked;
    for (const { key } of keys) {
      state.tradeStyles[key] = next;
      const chk = keyChecks.get(key);
      if (chk) chk.checked = next;
    }
    redraw();
  };

  for (const { key, label } of keys) {
    const row = document.createElement("label");
    row.className = "chart-control-row";
    const chk = document.createElement("input");
    chk.type = "checkbox";
    chk.checked = state.tradeStyles[key];
    chk.onchange = () => {
      state.tradeStyles[key] = chk.checked;
      refreshAll();
      redraw();
    };
    keyChecks.set(key, chk);
    const txt = document.createElement("span");
    txt.innerText = label;
    row.appendChild(chk);
    row.appendChild(txt);
    styleWrap.appendChild(row);
  }

  const presetsWrap = document.createElement("div");
  presetsWrap.className = "chart-control-group";

  const presetsLabel = document.createElement("span");
  presetsLabel.innerText = "Presets";
  presetsLabel.className = "chart-control-label";
  presetsWrap.appendChild(presetsLabel);

  const applyPreset = (next: Partial<Record<TradeStyleKey, boolean>>) => {
    for (const { key } of keys) {
      const value = next[key] ?? false;
      state.tradeStyles[key] = value;
      const chk = keyChecks.get(key);
      if (chk) chk.checked = value;
    }
    refreshAll();
    redraw();
  };

  const presets: Array<{
    label: string;
    styles: Partial<Record<TradeStyleKey, boolean>>;
  }> = [
    {
      label: "Minimal",
      styles: {
        pin: true,
        vertical: false,
        zone: false,
        countdown: false,
        animation: false,
      },
    },
    {
      label: "Classic",
      styles: {
        pin: true,
        vertical: true,
        zone: true,
        countdown: true,
        animation: false,
      },
    },
    {
      label: "Pro",
      styles: {
        pin: true,
        vertical: true,
        zone: true,
        countdown: true,
        animation: true,
      },
    },
    {
      label: "Animated",
      styles: {
        pin: true,
        vertical: false,
        zone: true,
        countdown: true,
        animation: true,
      },
    },
  ];

  for (const preset of presets) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.innerText = preset.label;
    btn.className = "chart-config-button";
    btn.onclick = () => applyPreset(preset.styles);
  }

  styleWrap.appendChild(presetsWrap);
  container.appendChild(styleWrap);

  return container;
}


export function createTradePanel(
  state: State,
  redraw: () => void,
  setTimeframe?: (tf: Timeframe) => void,  // Add optional setTimeframe parameter
): HTMLElement {
  const panel = document.createElement("aside");
  panel.className = "chart-trade-panel";

  let durationSeconds = 60;
  let stake = 10;

  // Contract type selector
  const contractTypeSection = document.createElement("div");
  contractTypeSection.className = "chart-contract-type-section";
  
  const contractTypeLabel = document.createElement("div");
  contractTypeLabel.className = "chart-section-label";
  contractTypeLabel.innerHTML = `<span>CONTRACT TYPE</span><small>Select trade type</small>`;
  contractTypeSection.appendChild(contractTypeLabel);
  
  const contractTypeSelect = document.createElement("select");
  contractTypeSelect.className = "chart-config-select";
  contractTypeSelect.style.width = "100%";
  contractTypeSelect.style.marginBottom = "8px";
  
  // ALL contract types - organized
  const contractGroups = [
    { label: "SIMPLE", types: [
      { value: "UP", label: "UP - Price goes up" },
      { value: "DOWN", label: "DOWN - Price goes down" }
    ]},
    { label: "SINGLE BARRIER", types: [
      { value: "HIGH", label: "HIGH - Exit > Barrier" },
      { value: "LOW", label: "LOW - Exit < Barrier" }
    ]},
    { label: "DOUBLE BARRIER (Exit based)", types: [
      { value: "ENDS_BETWEEN", label: "ENDS BETWEEN - Exit between barriers" },
      { value: "ENDS_OUTSIDE", label: "ENDS OUTSIDE - Exit outside barriers" }
    ]},
    { label: "PATH DEPENDENT (Touch based)", types: [
      { value: "KNOCK_IN", label: "KNOCK IN - Touch barrier = Win" },
      { value: "KNOCK_OUT", label: "KNOCK OUT - Touch barrier = Loss" }
    ]},
    { label: "PATH DEPENDENT (Range based)", types: [
      { value: "STAYS_IN", label: "STAYS IN - Stay inside range = Win" },
      { value: "STAYS_OUT", label: "STAYS OUT - Stay outside range = Win" }
    ]},
    { label: "DIGITS", types: [
      { value: "MATCHES", label: "MATCHES - Last digit matches target" },
      { value: "DIFFERS", label: "DIFFERS - Last digit differs from target" },
      { value: "EVEN", label: "EVEN - Last digit is even (0,2,4,6,8)" },
      { value: "ODD", label: "ODD - Last digit is odd (1,3,5,7,9)" },
      { value: "PRIME", label: "PRIME - Last digit is prime (2,3,5,7)" },
      { value: "NON_PRIME", label: "NON_PRIME - Last digit is not prime" },
      { value: "OVER", label: "OVER - Last digit > target digit" },
      { value: "UNDER", label: "UNDER - Last digit < target digit" },
      { value: "RANGE_IN", label: "RANGE IN - Last digit between low-high" },
      { value: "RANGE_OUT", label: "RANGE OUT - Last digit outside low-high" }
    ]}
  ];
  
  for (const group of contractGroups) {
    const optgroup = document.createElement("optgroup");
    optgroup.label = group.label;
    for (const type of group.types) {
      const option = document.createElement("option");
      option.value = type.value;
      option.innerText = type.label;
      optgroup.appendChild(option);
    }
    contractTypeSelect.appendChild(optgroup);
  }
  
  contractTypeSection.appendChild(contractTypeSelect);
  
  // Dynamic inputs container
  const inputsSection = document.createElement("div");
  inputsSection.className = "chart-inputs-section";
  
  // Single barrier input
  const singleBarrierRow = document.createElement("div");
  singleBarrierRow.className = "chart-input-group";
  const singleBarrierLabel = document.createElement("label");
  singleBarrierLabel.innerText = "Barrier Price";
  singleBarrierLabel.className = "chart-input-label";
  const singleBarrierInput = document.createElement("input");
  singleBarrierInput.type = "number";
  singleBarrierInput.placeholder = "Enter price level";
  singleBarrierInput.step = "0.01";
  singleBarrierInput.className = "chart-input-field";
  singleBarrierRow.appendChild(singleBarrierLabel);
  singleBarrierRow.appendChild(singleBarrierInput);
  
  // Double barrier inputs
  const doubleBarrierRow = document.createElement("div");
  doubleBarrierRow.className = "chart-double-input-group";
  
  const lowGroup = document.createElement("div");
  lowGroup.className = "chart-input-group";
  const lowBarrierLabel = document.createElement("label");
  lowBarrierLabel.innerText = "Low Barrier";
  lowBarrierLabel.className = "chart-input-label";
  const lowBarrierInput = document.createElement("input");
  lowBarrierInput.type = "number";
  lowBarrierInput.placeholder = "Lower bound";
  lowBarrierInput.step = "0.01";
  lowBarrierInput.className = "chart-input-field";
  lowGroup.appendChild(lowBarrierLabel);
  lowGroup.appendChild(lowBarrierInput);
  
  const highGroup = document.createElement("div");
  highGroup.className = "chart-input-group";
  const highBarrierLabel = document.createElement("label");
  highBarrierLabel.innerText = "High Barrier";
  highBarrierLabel.className = "chart-input-label";
  const highBarrierInput = document.createElement("input");
  highBarrierInput.type = "number";
  highBarrierInput.placeholder = "Upper bound";
  highBarrierInput.step = "0.01";
  highBarrierInput.className = "chart-input-field";
  highGroup.appendChild(highBarrierLabel);
  highGroup.appendChild(highBarrierInput);
  
  doubleBarrierRow.appendChild(lowGroup);
  doubleBarrierRow.appendChild(highGroup);
  
  // Digit target input
  const digitTargetRow = document.createElement("div");
  digitTargetRow.className = "chart-input-group";
  const digitTargetLabel = document.createElement("label");
  digitTargetLabel.innerText = "Target Digit (0-9)";
  digitTargetLabel.className = "chart-input-label";
  const digitTargetInput = document.createElement("input");
  digitTargetInput.type = "number";
  digitTargetInput.min = "0";
  digitTargetInput.max = "9";
  digitTargetInput.placeholder = "0-9";
  digitTargetInput.step = "1";
  digitTargetInput.className = "chart-input-field";
  digitTargetRow.appendChild(digitTargetLabel);
  digitTargetRow.appendChild(digitTargetInput);
  
  // Digit range inputs
  const digitRangeRow = document.createElement("div");
  digitRangeRow.className = "chart-double-input-group";
  
  const digitLowGroup = document.createElement("div");
  digitLowGroup.className = "chart-input-group";
  const digitLowLabel = document.createElement("label");
  digitLowLabel.innerText = "Low Digit (0-9)";
  digitLowLabel.className = "chart-input-label";
  const digitLowInput = document.createElement("input");
  digitLowInput.type = "number";
  digitLowInput.min = "0";
  digitLowInput.max = "9";
  digitLowInput.placeholder = "Low";
  digitLowInput.step = "1";
  digitLowInput.className = "chart-input-field";
  digitLowGroup.appendChild(digitLowLabel);
  digitLowGroup.appendChild(digitLowInput);
  
  const digitHighGroup = document.createElement("div");
  digitHighGroup.className = "chart-input-group";
  const digitHighLabel = document.createElement("label");
  digitHighLabel.innerText = "High Digit (0-9)";
  digitHighLabel.className = "chart-input-label";
  const digitHighInput = document.createElement("input");
  digitHighInput.type = "number";
  digitHighInput.min = "0";
  digitHighInput.max = "9";
  digitHighInput.placeholder = "High";
  digitHighInput.step = "1";
  digitHighInput.className = "chart-input-field";
  digitHighGroup.appendChild(digitHighLabel);
  digitHighGroup.appendChild(digitHighInput);
  
  digitRangeRow.appendChild(digitLowGroup);
  digitRangeRow.appendChild(digitHighGroup);
  
  inputsSection.appendChild(singleBarrierRow);
  inputsSection.appendChild(doubleBarrierRow);
  inputsSection.appendChild(digitTargetRow);
  inputsSection.appendChild(digitRangeRow);
  
  // Duration section
  const durationSection = document.createElement("div");
  durationSection.className = "chart-duration-section";
  const durationLabel = document.createElement("div");
  durationLabel.className = "chart-section-label";
  durationLabel.innerHTML = `<span>DURATION</span><small>Contract length</small>`;
  durationSection.appendChild(durationLabel);
  
  // Build panel
  const headerDiv = document.createElement("div");
  headerDiv.className = "chart-contract-head";
  const headerIcon = document.createElement("span");
  headerIcon.className = "chart-contract-icon";
  headerIcon.innerText = "↑↓";
  const headerStrong = document.createElement("strong");
  headerStrong.innerText = "Trade Panel";
  headerDiv.appendChild(headerIcon);
  headerDiv.appendChild(headerStrong);
  
  panel.innerHTML = "";
  panel.appendChild(headerDiv);
  panel.appendChild(contractTypeSection);
  panel.appendChild(inputsSection);
  panel.appendChild(durationSection);
  
  const segment = document.createElement("div");
  segment.className = "chart-segment";
  const durationTab = document.createElement("button");
  durationTab.type = "button";
  durationTab.className = "is-active";
  durationTab.innerText = "Duration";
  const endTimeTab = document.createElement("button");
  endTimeTab.type = "button";
  endTimeTab.innerText = "End time";
  segment.appendChild(durationTab);
  segment.appendChild(endTimeTab);
  durationSection.appendChild(segment);
  
  const durationGrid = document.createElement("div");
  durationGrid.className = "chart-duration-grid";
  const durationButtons = new Map<number, HTMLButtonElement>();
  for (const option of [
    { label: "5s", value: 5 },
    { label: "15s", value: 15 },
    { label: "30s", value: 30 },
    { label: "1m", value: 60 },
    { label: "2m", value: 120 },
    { label: "5m", value: 300 },
  ]) {
    const durationButton = document.createElement("button");
    durationButton.type = "button";
    durationButton.innerText = option.label;
    durationButton.onclick = () => {
      durationSeconds = option.value;
      refresh();
    };
    durationButtons.set(option.value, durationButton);
    durationGrid.appendChild(durationButton);
  }
  durationSection.appendChild(durationGrid);
  
  const stakeRow = document.createElement("div");
  stakeRow.className = "chart-stake-row";
  const minus = document.createElement("button");
  minus.type = "button";
  minus.innerText = "−";
  const valueSpan = document.createElement("strong");
  valueSpan.innerText = `${stake} USD`;
  const plus = document.createElement("button");
  plus.type = "button";
  plus.innerText = "+";
  minus.onclick = () => {
    stake = Math.max(1, stake - 1);
    refresh();
  };
  plus.onclick = () => {
    stake += 1;
    refresh();
  };
  stakeRow.appendChild(minus);
  stakeRow.appendChild(valueSpan);
  stakeRow.appendChild(plus);
  durationSection.appendChild(stakeRow);
  
  const metrics = document.createElement("div");
  metrics.className = "chart-metrics";
  const payoutSpan = document.createElement("span");
  payoutSpan.innerHTML = `<small>PAYOUT</small><strong>19.42 USD</strong>`;
  const winRateSpan = document.createElement("span");
  winRateSpan.innerHTML = `<small>WIN RATE</small><strong>94.20%</strong>`;
  metrics.appendChild(payoutSpan);
  metrics.appendChild(winRateSpan);
  durationSection.appendChild(metrics);
  
  const actions = document.createElement("div");
  actions.className = "chart-trade-actions";
  const rise = document.createElement("button");
  rise.type = "button";
  rise.className = "chart-rise";
  rise.innerText = "BUY / RISE";
  const fall = document.createElement("button");
  fall.type = "button";
  fall.className = "chart-fall";
  fall.innerText = "SELL / FALL";
  
  // Helper to get values based on contract type
  const getInputValues = () => {
    const type = contractTypeSelect.value;
    const needsSingle = ["HIGH", "LOW", "KNOCK_IN", "KNOCK_OUT"].includes(type);
    const needsDouble = ["ENDS_BETWEEN", "ENDS_OUTSIDE", "STAYS_IN", "STAYS_OUT"].includes(type);
    const needsDigitTarget = ["MATCHES", "DIFFERS", "OVER", "UNDER"].includes(type);
    const needsDigitRange = ["RANGE_IN", "RANGE_OUT"].includes(type);
    
    return {
      barrier: needsSingle ? parseFloat(singleBarrierInput.value) : undefined,
      barrierLow: needsDouble ? parseFloat(lowBarrierInput.value) : undefined,
      barrierHigh: needsDouble ? parseFloat(highBarrierInput.value) : undefined,
      digitTarget: needsDigitTarget ? parseInt(digitTargetInput.value) : undefined,
      digitLow: needsDigitRange ? parseInt(digitLowInput.value) : undefined,
      digitHigh: needsDigitRange ? parseInt(digitHighInput.value) : undefined,
    };
  };
  
  // Validate inputs based on contract type
  const validateInputs = (type: string, values: any): boolean => {
    if (["HIGH", "LOW", "KNOCK_IN", "KNOCK_OUT"].includes(type)) {
      if (!values.barrier || isNaN(values.barrier)) {
        alert("Please enter a barrier price");
        return false;
      }
    }
    if (["ENDS_BETWEEN", "ENDS_OUTSIDE", "STAYS_IN", "STAYS_OUT"].includes(type)) {
      if (!values.barrierLow || !values.barrierHigh || isNaN(values.barrierLow) || isNaN(values.barrierHigh)) {
        alert("Please enter both low and high barrier prices");
        return false;
      }
      if (values.barrierLow >= values.barrierHigh) {
        alert("Low barrier must be less than high barrier");
        return false;
      }
    }
    if (["MATCHES", "DIFFERS", "OVER", "UNDER"].includes(type)) {
      if (isNaN(values.digitTarget) || values.digitTarget < 0 || values.digitTarget > 9) {
        alert("Please enter a target digit between 0 and 9");
        return false;
      }
    }
    if (["RANGE_IN", "RANGE_OUT"].includes(type)) {
      if (isNaN(values.digitLow) || isNaN(values.digitHigh) || values.digitLow < 0 || values.digitHigh > 9) {
        alert("Please enter low and high digits between 0 and 9");
        return false;
      }
      if (values.digitLow >= values.digitHigh) {
        alert("Low digit must be less than high digit");
        return false;
      }
    }
    return true;
  };
  
  const executeTrade = (direction: TradeDirection, type: string, values: any, lastPrice: number) => {
    addContractMarker(state, {
      contractType: type,
      direction,
      duration: durationSeconds,
      durationUnit: "s",
      entryPrice: lastPrice,
      ...(values.barrier !== undefined && { barrier: values.barrier }),
      ...(values.barrierLow !== undefined && { barrierLow: values.barrierLow }),
      ...(values.barrierHigh !== undefined && { barrierHigh: values.barrierHigh }),
      ...(values.digitTarget !== undefined && { digitTarget: values.digitTarget }),
      ...(values.digitLow !== undefined && { digitLow: values.digitLow }),
      ...(values.digitHigh !== undefined && { digitHigh: values.digitHigh }),
    });
    
    goToLatest(state, 0.3);
    redraw();
  };
  

const placeTrade = (direction: TradeDirection) => {
  const lastPrice = state.chartData[state.chartData.length - 1]?.quote;
  if (!lastPrice) {
    alert("No price data available");
    return;
  }
  
  const type = contractTypeSelect.value;
  const values = getInputValues();
  
  if (!validateInputs(type, values)) return;
  
  // Auto-suggest barrier values based on current price
  if (["HIGH", "KNOCK_IN"].includes(type) && (!values.barrier || isNaN(values.barrier))) {
    values.barrier = lastPrice + 0.5;
    singleBarrierInput.value = values.barrier.toString();
  }
  if (["LOW", "KNOCK_OUT"].includes(type) && (!values.barrier || isNaN(values.barrier))) {
    values.barrier = lastPrice - 0.5;
    singleBarrierInput.value = values.barrier.toString();
  }
  if (["ENDS_BETWEEN", "ENDS_OUTSIDE", "STAYS_IN", "STAYS_OUT"].includes(type)) {
    if (!values.barrierLow || isNaN(values.barrierLow)) {
      values.barrierLow = lastPrice - 0.5;
      lowBarrierInput.value = values.barrierLow.toString();
    }
    if (!values.barrierHigh || isNaN(values.barrierHigh)) {
      values.barrierHigh = lastPrice + 0.5;
      highBarrierInput.value = values.barrierHigh.toString();
    }
  }
  
  // Auto-suggest digit values
  if (["OVER", "UNDER", "MATCHES", "DIFFERS"].includes(type)) {
    if (values.digitTarget === undefined || isNaN(values.digitTarget)) {
      values.digitTarget = 5;
      digitTargetInput.value = values.digitTarget.toString();
    }
  }
  if (["RANGE_IN", "RANGE_OUT"].includes(type)) {
    if (values.digitLow === undefined || isNaN(values.digitLow)) {
      values.digitLow = 3;
      digitLowInput.value = values.digitLow.toString();
    }
    if (values.digitHigh === undefined || isNaN(values.digitHigh)) {
      values.digitHigh = 7;
      digitHighInput.value = values.digitHigh.toString();
    }
  }
  
  // Auto-switch to 1t timeframe for short duration trades (less than 60 seconds)
  const durationMs = durationSeconds * 1000;
  if (durationMs < 60000 && state.timeframe !== "1t" && setTimeframe) {
    // Automatically switch to 1t timeframe without prompting
    setTimeframe("1t");
    // Small delay to allow timeframe to apply before placing trade
    setTimeout(() => {
      executeTrade(direction, type, values, lastPrice);
    }, 100);
    return;
  }
  
  // Place trade normally
  executeTrade(direction, type, values, lastPrice);
};
  
  rise.onclick = () => placeTrade("buy");
  fall.onclick = () => placeTrade("sell");
  actions.appendChild(rise);
  actions.appendChild(fall);
  durationSection.appendChild(actions);
  
  // Marker styles section (same as before)
  const markerPanel = document.createElement("section");
  markerPanel.className = "chart-marker-panel";
  
  const markerHead = document.createElement("div");
  markerHead.className = "chart-marker-head";
  const markerHeadStrong = document.createElement("strong");
  markerHeadStrong.innerText = "MARKER STYLE";
  const markerHeadSpan = document.createElement("span");
  markerHeadSpan.innerText = "Chart display";
  markerHead.appendChild(markerHeadStrong);
  markerHead.appendChild(markerHeadSpan);
  
  const markerPresets = document.createElement("div");
  markerPresets.className = "chart-marker-presets";
  const markerOptions = document.createElement("div");
  markerOptions.className = "chart-marker-options";
  
  const styleKeys: Array<{ key: TradeStyleKey; label: string }> = [
    { key: "pin", label: "Pin" },
    { key: "vertical", label: "Vertical" },
    { key: "zone", label: "Zone" },
    { key: "countdown", label: "Countdown" },
    { key: "animation", label: "Animation" },
  ];
  
  const styleChecks = new Map<TradeStyleKey, HTMLInputElement>();
  const presets = [
    { label: "Minimal", styles: { pin: true } },
    { label: "Classic", styles: { pin: true, vertical: true, zone: true, countdown: true } },
    { label: "Pro", styles: { pin: true, vertical: true, zone: true, countdown: true, animation: true } },
    { label: "Animated", styles: { pin: true, zone: true, countdown: true, animation: true } },
  ];
  
  const applyPreset = (next: Partial<Record<TradeStyleKey, boolean>>) => {
    for (const { key } of styleKeys) {
      state.tradeStyles[key] = next[key] ?? false;
    }
    refresh();
    redraw();
  };
  
  for (const preset of presets) {
    const presetButton = document.createElement("button");
    presetButton.type = "button";
    presetButton.innerText = preset.label;
    presetButton.className = "chart-config-button";
    presetButton.style.fontSize = "10px";
    presetButton.style.padding = "4px 8px";
    presetButton.onclick = () => applyPreset(preset.styles);
    markerPresets.appendChild(presetButton);
  }
  
  for (const { key, label } of styleKeys) {
    const item = document.createElement("label");
    item.className = "chart-marker-option";
    const check = document.createElement("input");
    check.type = "checkbox";
    check.checked = state.tradeStyles[key];
    check.onchange = () => {
      state.tradeStyles[key] = check.checked;
      refresh();
      redraw();
    };
    styleChecks.set(key, check);
    const text = document.createElement("span");
    text.innerText = label;
    item.appendChild(check);
    item.appendChild(text);
    markerOptions.appendChild(item);
  }
  
  markerPanel.appendChild(markerHead);
  markerPanel.appendChild(markerPresets);
  markerPanel.appendChild(markerOptions);
  panel.appendChild(markerPanel);
  
  // CSS styling (same as before)
  const style = document.createElement("style");
    style.textContent = `
      .chart-contract-type-section,
      .chart-inputs-section,
      .chart-duration-section {
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--chart-border);
      }
      
      .chart-section-label {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 10px;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.5px;
        color: var(--chart-faint);
        text-transform: uppercase;
      }
      
      .chart-section-label small {
        font-size: 9px;
        font-weight: 400;
        text-transform: none;
        color: var(--chart-muted);
      }
      
      .chart-input-group {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-bottom: 12px;
      }
      
      .chart-input-label {
        font-size: 10px;
        font-weight: 600;
        color: var(--chart-muted);
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
      
      .chart-input-field {
        width: 100%;
        padding: 10px 12px;
        background: var(--chart-control-hover);
        border: 1px solid var(--chart-border);
        border-radius: 6px;
        color: var(--chart-text);
        font-size: 12px;
        font-family: monospace;
        transition: all 0.15s ease;
        box-sizing: border-box;
      }
      
      .chart-input-field:focus {
        outline: none;
        border-color: var(--chart-teal);
        box-shadow: 0 0 0 2px rgba(var(--chart-teal-rgb), 0.1);
      }
      
      .chart-input-field[type="number"] {
        -moz-appearance: textfield;
        appearance: textfield;
      }
      
      .chart-input-field[type="number"]::-webkit-inner-spin-button,
      .chart-input-field[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      .chart-double-input-group {
        display: flex;
        gap: 12px;
        margin-bottom: 12px;
      }
      
      .chart-double-input-group .chart-input-group {
        flex: 1;
        margin-bottom: 0;
      }
      
      .chart-duration-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin: 12px 0;
      }
      
      .chart-duration-grid button {
        padding: 8px;
        background: var(--chart-control-hover);
        border: 1px solid var(--chart-border);
        border-radius: 6px;
        color: var(--chart-muted);
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
      }
      
      .chart-duration-grid button.is-active {
        color: var(--chart-text);
        border-color: var(--chart-teal);
        background: rgba(var(--chart-teal-rgb), 0.12);
      }
      
      .chart-stake-row {
        display: grid;
        grid-template-columns: 42px 1fr 42px;
        align-items: center;
        margin: 12px 0;
        background: var(--chart-control-hover);
        border: 1px solid var(--chart-border);
        border-radius: 8px;
      }
      
      .chart-stake-row button {
        height: 44px;
        background: transparent;
        border: none;
        color: var(--chart-text);
        font-size: 18px;
        font-weight: 600;
        cursor: pointer;
      }
      
      .chart-stake-row strong {
        text-align: center;
        font-size: 14px;
        font-weight: 700;
      }
      
      .chart-metrics {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin: 12px 0;
      }
      
      .chart-metrics span {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 12px;
        background: var(--chart-control-hover);
        border: 1px solid var(--chart-border);
        border-radius: 8px;
        text-align: center;
      }
      
      .chart-metrics small {
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.5px;
        color: var(--chart-faint);
        text-transform: uppercase;
      }
      
      .chart-metrics strong {
        font-size: 15px;
        font-weight: 800;
        color: var(--chart-teal);
      }
      
      .chart-trade-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin: 12px 0;
      }
      
      .chart-trade-actions button {
        padding: 14px;
        border: none;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 0.5px;
        cursor: pointer;
        transition: all 0.15s ease;
      }
      
      .chart-rise {
        background: linear-gradient(135deg, var(--chart-teal), color-mix(in srgb, var(--chart-teal) 80%, #000));
        color: white;
      }
      
      .chart-fall {
        background: linear-gradient(135deg, var(--chart-danger), color-mix(in srgb, var(--chart-danger) 80%, #000));
        color: white;
      }
      
      .chart-marker-panel {
        margin-top: 16px;
        padding-top: 12px;
        border-top: 1px solid var(--chart-border);
      }
      
      .chart-marker-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
      }
      
      .chart-marker-head strong {
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.5px;
        color: var(--chart-faint);
        text-transform: uppercase;
      }
      
      .chart-marker-head span {
        font-size: 9px;
        color: var(--chart-muted);
      }
      
      .chart-marker-presets {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        margin-bottom: 12px;
      }
      
      .chart-marker-presets button {
        padding: 8px;
        background: var(--chart-control-hover);
        border: 1px solid var(--chart-border);
        border-radius: 6px;
        color: var(--chart-muted);
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
      }
      
      .chart-marker-presets button:hover {
        border-color: var(--chart-teal);
        color: var(--chart-text);
      }
      
      .chart-marker-options {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
      }
      
      .chart-marker-option {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        background: var(--chart-control-hover);
        border: 1px solid var(--chart-border);
        border-radius: 6px;
        font-size: 11px;
        cursor: pointer;
        transition: all 0.15s ease;
      }
      
      .chart-marker-option:has(input:checked) {
        border-color: var(--chart-teal);
        background: rgba(var(--chart-teal-rgb), 0.08);
      }
      
      .chart-marker-option input {
        accent-color: var(--chart-teal);
        margin: 0;
      }
      
      .chart-contract-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        background: var(--chart-teal-soft);
        border-radius: 8px;
        font-size: 18px;
        font-weight: 700;
        color: var(--chart-teal);
      }
      
      .chart-contract-head {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--chart-border);
      }
      
      .chart-contract-head strong {
        font-size: 14px;
        font-weight: 800;
        color: var(--chart-text);
      }
    `;
  panel.appendChild(style);
  
  const updateInputsVisibility = () => {
    const type = contractTypeSelect.value;
    const needsSingle = ["HIGH", "LOW", "KNOCK_IN", "KNOCK_OUT"].includes(type);
    const needsDouble = ["ENDS_BETWEEN", "ENDS_OUTSIDE", "STAYS_IN", "STAYS_OUT"].includes(type);
    const needsDigitTarget = ["MATCHES", "DIFFERS", "OVER", "UNDER"].includes(type);
    const needsDigitRange = ["RANGE_IN", "RANGE_OUT"].includes(type);
    
    singleBarrierRow.style.display = needsSingle ? "flex" : "none";
    doubleBarrierRow.style.display = needsDouble ? "flex" : "none";
    digitTargetRow.style.display = needsDigitTarget ? "flex" : "none";
    digitRangeRow.style.display = needsDigitRange ? "flex" : "none";
    
    // Update labels based on contract type
    if (type === "OVER") {
      digitTargetLabel.innerText = "Target Digit (0-9) - Last digit must be GREATER";
    } else if (type === "UNDER") {
      digitTargetLabel.innerText = "Target Digit (0-9) - Last digit must be LESS";
    } else if (type === "MATCHES") {
      digitTargetLabel.innerText = "Target Digit (0-9) - Last digit MUST MATCH";
    } else if (type === "DIFFERS") {
      digitTargetLabel.innerText = "Target Digit (0-9) - Last digit MUST DIFFER";
    } else if (type === "RANGE_IN") {
      digitLowLabel.innerText = "Low Digit - Start of range";
      digitHighLabel.innerText = "High Digit - End of range";
    } else if (type === "RANGE_OUT") {
      digitLowLabel.innerText = "Low Digit - Outside below this";
      digitHighLabel.innerText = "High Digit - Outside above this";
    } else {
      digitTargetLabel.innerText = "Target Digit (0-9)";
      digitLowLabel.innerText = "Low Digit (0-9)";
      digitHighLabel.innerText = "High Digit (0-9)";
    }
  };
  
  contractTypeSelect.onchange = () => {
    state.selectedContractType = contractTypeSelect.value as ContractType;
    updateInputsVisibility();
    redraw();
  };
  
  function refresh() {
    durationButtons.forEach((button, duration) => {
      button.classList.toggle("is-active", duration === durationSeconds);
    });
    valueSpan.innerText = `${stake} USD`;
    styleChecks.forEach((check, key) => {
      check.checked = state.tradeStyles[key];
    });
    updateInputsVisibility();
  }
  
  refresh();
  return panel;
}

export function createMarketControls(options: MarketControlsOptions): HTMLDivElement {
  const { redraw, liveData = liveDataManager, marketSelection, onSelectSymbol } = options.data;

  const root = document.createElement("div");
  root.className = "chart-market-controls";

  const buttonEl = button("chart-market-card", "", "Change market");
  const list = document.createElement("div");
  list.className = "chart-market-list";
  let open = false;

  const renderButton = () => {
    const selectedSymbol = marketSelection?.symbol ?? liveData.getSelectedSymbol();
    const selectedMarket = liveData
      .getSymbols()
      .find((item) => item.symbol === selectedSymbol || item.name === selectedSymbol);
    const label = getMarketLabel(selectedMarket);
    const code = getMarketCode(selectedMarket) || selectedSymbol;
    const status =
      selectedMarket?.exchange_is_open === 1 && selectedMarket?.is_active !== false ? "Live" : "Closed";

    buttonEl.replaceChildren();
    const marketIcon = document.createElement("span");
    marketIcon.className = "chart-market-icon";
    marketIcon.appendChild(createIcon("chart"));
    buttonEl.appendChild(marketIcon);

    const copy = document.createElement("span");
    copy.className = "chart-market-copy";
    appendTextTag(copy, "strong", label);
    const meta = appendTextTag(copy, "span", `${code} `);
    appendTextTag(meta, "em", status);
    buttonEl.appendChild(copy);

    const chevron = document.createElement("span");
    chevron.className = "chart-chevron";
    chevron.appendChild(createIcon("chevronDown"));
    buttonEl.appendChild(chevron);
  };

  const renderList = () => {
    list.replaceChildren();
    list.style.display = open ? "block" : "none";
    if (!open) return;

    const search = document.createElement("input");
    search.className = "chart-search";
    search.placeholder = "Search market";
    list.appendChild(search);

    const rowsHost = document.createElement("div");
    const renderMarkets = () => {
      rowsHost.replaceChildren();
      const query = search.value.trim().toLowerCase();
      const selectedSymbol = marketSelection?.symbol ?? liveData.getSelectedSymbol();
      const markets = liveData
        .getSymbols()
        .filter((item) => item.is_active !== false)
        .filter((item) => {
          if (!query) return true;
          const label = getMarketLabel(item).toLowerCase();
          const code = getMarketCode(item).toLowerCase();
          const market = String(item.market ?? "").toLowerCase();
          const subgroup = String(item.subgroup ?? "").toLowerCase();
          return label.includes(query) || code.includes(query) || market.includes(query) || subgroup.includes(query);
        })
        .sort((a, b) => {
          const aSelected = a.symbol === selectedSymbol || a.name === selectedSymbol;
          const bSelected = b.symbol === selectedSymbol || b.name === selectedSymbol;
          if (aSelected !== bSelected) return aSelected ? -1 : 1;
          return getMarketLabel(a).localeCompare(getMarketLabel(b));
        });

      if (markets.length === 0) {
        const empty = document.createElement("div");
        empty.className = "chart-empty-state";
        empty.innerText = query ? "No markets match your search" : "Loading markets...";
        rowsHost.appendChild(empty);
        return;
      }

      for (const market of markets) {
        const code = getMarketCode(market) || market.symbol || market.name || "";
        const row = button("chart-menu-row", "");
        const rowIcon = document.createElement("span");
        rowIcon.appendChild(createIcon("chart"));
        row.appendChild(rowIcon);
        appendTextTag(row, "strong", getMarketLabel(market));
        appendTextTag(row, "em", code);
        row.classList.toggle("is-active", market.symbol === selectedSymbol || market.name === selectedSymbol);
        row.onclick = () => {
          if (code) {
            onSelectSymbol?.(code);
            liveData.selectSymbol(code);
            if (marketSelection) marketSelection.symbol = code;
          }
          open = false;
          renderButton();
          renderList();
          redraw();
        };
        rowsHost.appendChild(row);
      }
    };

    search.oninput = () => renderMarkets();
    rowsHost.className = "chart-market-list";
    list.appendChild(rowsHost);
    renderMarkets();
  };

  buttonEl.onclick = () => {
    open = !open;
    renderButton();
    renderList();
  };

  (root as any).updateMarketButton = () => {
    renderButton();
  };

  const marketListener = () => {
    renderButton();
    renderList();
  };
  liveData.addMarketListener(marketListener);

  (root as HTMLDivElement & { destroy?: () => void }).destroy = () => {
    liveData.removeMarketListener(marketListener);
  };

  renderButton();
  renderList();
  root.append(buttonEl, list);
  return root;
}

export function createTimeframeControls(options: TimeframeControlsOptions): HTMLDivElement {
  const { state, redraw, setTimeframe } = options.data;
  const root = document.createElement("div");
  root.className = "chart-timeframe-controls";

  const frameBar = document.createElement("div");
  frameBar.className = "chart-timeframe-menu";
  const buttons = new Map<Timeframe, HTMLButtonElement>();

  for (const tf of TIMEFRAMES) {
    const tfButton = button("chart-timeframe-option", tf, `${tf} timeframe`);
    const disabled = tf === "1t" && isCandleBasedChartType(state.chartType);
    tfButton.disabled = disabled;
    tfButton.classList.toggle("is-disabled", disabled);
    tfButton.title = disabled
      ? "1 tick is unavailable for candle-based chart types."
      : `${tf} timeframe`;
    tfButton.style.opacity = disabled ? "0.45" : "1";
    tfButton.style.cursor = disabled ? "not-allowed" : "pointer";
    tfButton.onclick = () => {
      if (tfButton.disabled) return;
      setTimeframe(tf);
      refresh();
      redraw();
    };
    buttons.set(tf, tfButton);
    frameBar.appendChild(tfButton);
  }

  function refresh() {
    buttons.forEach((el, tf) => {
      el.classList.toggle("is-active", state.timeframe === tf);
      const disabled = tf === "1t" && isCandleBasedChartType(state.chartType);
      el.disabled = disabled;
      el.classList.toggle("is-disabled", disabled);
      el.style.opacity = disabled ? "0.45" : "1";
      el.style.cursor = disabled ? "not-allowed" : "pointer";
    });
  }

  refresh();
  root.appendChild(frameBar);
  return root;
}

export function createSettingsControls(options: SettingsControlsOptions): HTMLDivElement {
  const { state, redraw, setTheme } = options.data;
  const root = document.createElement("div");
  root.className = "chart-settings-controls";

  const panel = makePanel("Settings", "Chart appearance");

  const themeGrid = document.createElement("div");
  themeGrid.className = "chart-theme-grid";
  const themeTiles: HTMLButtonElement[] = [];
  function refresh() {
    themeTiles.forEach((tile, index) => {
      tile.classList.toggle("is-active", CHART_THEMES[index]?.id === state.themeName);
    });
  }
  for (const theme of CHART_THEMES) {
    const token = getThemeByName(theme.id);
    const tile = button("chart-theme-tile", "", theme.label);
    const swatch = document.createElement("span");
    swatch.className = "chart-theme-swatch";
    swatch.style.background = token.background;
    const upDot = document.createElement("span");
    upDot.style.background = token.up;
    const downDot = document.createElement("span");
    downDot.style.background = token.down;
    swatch.append(upDot, downDot);
    const textWrap = document.createElement("div");
    textWrap.className = "chart-theme-tile-copy";
    const title = document.createElement("strong");
    title.innerText = theme.label;
    const desc = document.createElement("span");
    desc.innerText = theme.id === "dark"
      ? "Deep contrast, clean panels"
      : theme.id === "moon"
        ? "Soft blue night palette"
        : theme.id === "matrix"
          ? "Glow with terminal energy"
          : theme.id === "lovely"
            ? "Soft editorial pinks"
            : theme.id === "summer"
              ? "Warm paper and copper"
              : "Neutral chart workspace";
    textWrap.append(title, desc);
    tile.append(swatch, textWrap);
    tile.onclick = () => {
      state.themeName = theme.id;
      setTheme?.(theme.id);
      applyThemeToInterface(theme.id);
      markStaticDirty();
      redraw();
      refresh();
    };
    themeGrid.appendChild(tile);
    themeTiles.push(tile);
  }
  panel.appendChild(themeGrid);

  const displaySection = document.createElement("div");
  displaySection.className = "chart-settings-block";
  appendTextTag(displaySection, "div", "Display", "chart-section-title");
  const displayOptions: Array<{
    key: keyof State["displaySettings"];
    label: string;
  }> = [
    { key: "showTimeGrid", label: "Time grid" },
    { key: "showPriceGrid", label: "Price grid" },
    { key: "showTimeAxis", label: "Time axis" },
    { key: "showPriceAxis", label: "Price axis" },
    { key: "showCrosshair", label: "Crosshair" },
    { key: "showLivePriceLine", label: "Live price line" },
  ];

  for (const { key, label } of displayOptions) {
    const item = document.createElement("label");
    item.className = "chart-setting-option";
    const check = document.createElement("input");
    check.type = "checkbox";
    check.checked = state.displaySettings[key];
    check.onchange = () => {
      state.displaySettings[key] = check.checked;
      if (key === "showTimeGrid" || key === "showPriceGrid" || key === "showTimeAxis" || key === "showPriceAxis") {
        markStaticDirty();
      }
      redraw();
    };
    const text = document.createElement("span");
    text.innerText = label;
    item.append(check, text);
    displaySection.appendChild(item);
  }

  const autoScale = document.createElement("label");
  autoScale.className = "chart-setting-option";
  const autoScaleCheck = document.createElement("input");
  autoScaleCheck.type = "checkbox";
  autoScaleCheck.checked = state.useDataRange;
  autoScaleCheck.onchange = () => {
    state.useDataRange = autoScaleCheck.checked;
    redraw();
  };
  const autoScaleText = document.createElement("span");
  autoScaleText.innerText = "Auto-scale price";
  autoScale.append(autoScaleCheck, autoScaleText);
  displaySection.appendChild(autoScale);

  panel.appendChild(displaySection);
  root.appendChild(panel);
  return root;
}
